import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  deleteDoc,
  updateDoc,
  onSnapshot,
  query,
  orderBy,
  limit,
  getCountFromServer,
  runTransaction,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Header from '@/components/layout/Header';
import { DashboardContentContainer } from '@/components/layout/DashboardContentContainer';
import {
  Plus,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Upload,
  FolderOpen,
  Download,
  History,
  Undo2,
  Copy,
  Pencil,
  Trash2,
  Users,
} from 'lucide-react';
import { showSuccess, showError, showWarning, confirmAction } from '@/lib/swal';
import Swal from 'sweetalert2';
import type { Event as DiningEvent, RSVP } from '@/types';
import { useAuth } from '@masonic-tools/shared-auth';
import { useTheme } from 'next-themes';
import PixiTablePlannerCanvas, {
  type TablePlannerStageShim,
  type KonvaGroupShim,
} from '@/components/table-planner/pixi/PixiTablePlannerCanvas';
import { TABLE_PLANNER_METRIC_STEP } from '@/components/table-planner/pixi/pixiTablePlannerDrawing';
import TableConfigModal, { TableConfig } from '@/components/table-planner/TableConfigModal';
import GuestAssignmentModal from '@/components/table-planner/GuestAssignmentModal';
import PDFExportModal from '@/components/table-planner/PDFExportModal';
import AutoPopulateModal from '@/components/table-planner/AutoPopulateModal';
import {
  buildGuestPartyColourMap,
  collectGuestRelationshipRoots,
  buildPrefixBorderColourMap,
  DEFAULT_OCCUPIED_SEAT_COLOURS,
  NEUTRAL_RELATIONSHIPS_OFF_SEAT_COLOURS,
  RELATIONSHIP_PARTY_PALETTE,
  RELATIONSHIP_PARTY_COLOUR_SCHEME_VERSION,
} from '@/components/table-planner/guestPartyColours';
import { useRanks } from '@/hooks/useRanks';
import { formatGuestListDisplayName } from '@/lib/rank-utils';
import { splitGuestNameIntoSeatLines } from '@/lib/seatLabelUtils';
import { recalculateSeatNumbers } from '@/lib/table-plan-seating';
import { isMultiTenantEvent } from '@/lib/ticket-mode';
import { expandMultiTenantGuestPlaceholders } from '@/lib/table-plan-guest-placeholders';
import { isSeatableRsvpData } from '@/lib/event-utils';
import { CommitNumberInput } from '@/components/table-planner/CommitNumberInput';
import {
  MAX_TABLE_SEATS,
  MIN_TABLE_SEATS,
  calculateSeatingPositions,
  getTablesBounds,
  mergeTables,
  reflowTable,
  removeGuestsFromTables,
  resizeTableSeats,
} from '@/components/table-planner/tableSeating';
// Helper function to remove undefined values from objects (Firestore doesn't support undefined)
function sanitizeForFirestore<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return null as any;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeForFirestore(item)) as any;
  }
  
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = sanitizeForFirestore(value);
      }
    }
    return cleaned;
  }
  
  return obj;
}

const TABLE_PLANNER_PLAYABLE_AREA_SIZE = 2000;
const MIN_TABLE_METRES = 0.5;
const MAX_TABLE_METRES = 5;

/** Match Tailwind `md`: guest list sidebar not shown below this width */
const TABLE_PLANNER_SIDEBAR_LAYOUT_MIN_PX = 768;

const devLog =
  import.meta.env.DEV ? (...args: unknown[]) => { console.log(...args); } : () => {};

/** Undo keeps the most recent table edits (pan/zoom is not undoable). */
const UNDO_HISTORY_LIMIT = 40;
/** Version snapshots kept per floor plan; older ones are pruned. */
const MAX_TABLE_PLAN_VERSIONS = 100;
const VERSIONS_LIST_LIMIT = 50;

type PlannerViewport = { scale: number; position: { x: number; y: number } };

type PlannerHistorySnapshot = {
  tables: TableConfig[];
  scale: number;
  stagePos: { x: number; y: number };
  capturedAt: string;
};

type VersionReason = 'autosave' | 'undo' | 'restore';

/**
 * Pan/zoom is personal: it lives in this browser (per floor plan) rather than in the shared plan
 * document, so one editor panning never moves anyone else's view.
 */
function viewportStorageKey(unitId: string, eventId: string, roomId: string) {
  return `tablePlanner:viewport:${unitId}:${eventId}:${roomId}`;
}

function readStoredViewport(key: string): PlannerViewport | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const scale = Number(parsed?.scale);
    const x = Number(parsed?.position?.x);
    const y = Number(parsed?.position?.y);
    if (![scale, x, y].every(Number.isFinite) || scale <= 0) return null;
    return { scale, position: { x, y } };
  } catch {
    return null;
  }
}

function writeStoredViewport(key: string, viewport: PlannerViewport) {
  try {
    window.localStorage.setItem(key, JSON.stringify(viewport));
  } catch {
    // Storage unavailable (private mode, quota): the view just won't be remembered.
  }
}

type TablePlanVersionEntry = PlannerHistorySnapshot & {
  id: string;
  createdAt: string;
  lastUpdated: string;
  reason?: string;
  createdByUid?: string;
};

/**
 * Firestore rules allow any signed-in user to read/write table plans (cross-tenant isolation is
 * enforced in the app), so only unit members and site admins get the planner.
 */
export default function VisualTablePlannerPage() {
  const { unitId, eventId } = useParams<{ unitId: string; eventId: string }>();
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  if (!userProfile) {
    return (
      <div className="flex h-screen flex-col bg-background text-foreground">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </div>
    );
  }

  const memberships: Array<{ unitId?: string; lodgeId?: string } | null | undefined> =
    userProfile.units || userProfile.lodges || [];
  const hasAccess =
    userProfile.isAdmin === true ||
    memberships.some((m) => m?.unitId === unitId || m?.lodgeId === unitId);

  if (!hasAccess) {
    return (
      <div className="flex h-screen flex-col bg-background text-foreground">
        <Header />
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
          <h1 className="text-xl font-semibold">You don't have access to this table plan</h1>
          <p className="max-w-md text-sm text-muted-foreground">
            Only members of the unit running this event can use its table planner.
          </p>
          <Button variant="outline" onClick={() => navigate(`/event/${eventId}`)}>
            Back to event
          </Button>
        </div>
      </div>
    );
  }

  return <VisualTablePlanner />;
}

function VisualTablePlanner() {
  const { unitId, eventId } = useParams<{ unitId: string; eventId: string }>();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { resolvedTheme } = useTheme();
  const plannerSurfaceScheme = resolvedTheme === 'dark' ? 'dark' : 'light';
  const { ranks } = useRanks();
  
  // Helper to check if user is admin for detailed logging
  const isAdmin = userProfile?.isAdmin === true;
  
  // Admin-only diagnostics helper
  const adminWarn = (...args: any[]) => {
    if (isAdmin) {
      console.warn('[ADMIN DEBUG - Table Planner]', ...args);
    }
  };

  const stageRef = useRef<TablePlannerStageShim | null>(null);
  const [loading, setLoading] = useState(true);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [liveSyncStatus, setLiveSyncStatus] = useState<'connecting' | 'live' | 'degraded'>('connecting');

  // Canvas state
  const [stageWidth, setStageWidth] = useState(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const reserveSidebar = w >= TABLE_PLANNER_SIDEBAR_LAYOUT_MIN_PX;
    return w - (reserveSidebar ? 400 : 100);
  });
  const [stageHeight, setStageHeight] = useState(window.innerHeight - 200);
  const [scale, setScale] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });

  // Tables and guests
  const [tables, setTables] = useState<TableConfig[]>([]);
  const tablesRef = useRef<TableConfig[]>(tables);
  const scaleRef = useRef(scale);
  const stagePosRef = useRef(stagePos);
  /**
   * The floor plan the `tables` state currently belongs to. Saves always target this room (never
   * `selectedRoomId`), so switching or deleting a plan can't write one plan's tables into another.
   * Null while no plan is loaded.
   */
  const tablesRoomIdRef = useRef<string | null>(null);
  /** Last tables confirmed on the server for `tablesRoomIdRef` — the base for merging remote edits. */
  const lastSavedTablesRef = useRef<TableConfig[]>([]);
  const lastSavedTablesSignatureRef = useRef('');
  /** Signature of tables applied from a remote snapshot or undo, so the undo tracker skips them. */
  const ignoreHistorySignatureRef = useRef<string | null>(null);
  const autoSaveInFlightRef = useRef(false);
  /** Settles when the in-flight save (and any save it queued) has finished. */
  const autoSaveDoneRef = useRef<Promise<void>>(Promise.resolve());
  const pendingAutoSaveRef = useRef(false);
  const pendingVersionReasonRef = useRef<VersionReason | null>(null);
  const liveSyncSourcesRef = useRef({
    rooms: false,
    currentRoom: false,
    allRooms: false,
  });
  tablesRef.current = tables;
  scaleRef.current = scale;
  stagePosRef.current = stagePos;

  const viewportPersistTimeoutRef = useRef<number | null>(null);
  /** True while the user is actively panning the canvas (Stage drag). Used to suppress deferred saves mid-gesture. */
  const isViewportPanningRef = useRef(false);
  /** Room whose viewport has been restored; the first snapshot of each room restores it once. */
  const viewportRoomIdRef = useRef<string | null>(null);

  const [rsvpGuests, setRsvpGuests] = useState<RSVP[]>([]);
  /** True once the RSVP list has been read from the server (not just the offline cache). */
  const [guestsLoadedFromServer, setGuestsLoadedFromServer] = useState(false);
  /** Null until the event has loaded; multi-tenant bookings add "Guest of …" placeholders per extra ticket. */
  const [isMultiTenant, setIsMultiTenant] = useState<boolean | null>(null);
  const allGuests = useMemo(
    () => (isMultiTenant ? expandMultiTenantGuestPlaceholders(rsvpGuests) : rsvpGuests),
    [isMultiTenant, rsvpGuests]
  );
  const [relationshipRootPaletteIndexMap, setRelationshipRootPaletteIndexMap] = useState<
    Map<string, number>
  >(new Map());
  const [selectedTableIds, setSelectedTableIds] = useState<Set<string>>(new Set());
  const [isDraggingTable, setIsDraggingTable] = useState(false);
  const [dragStartPositions, setDragStartPositions] = useState<Map<string, { x: number; y: number }>>(new Map());
  const dragMoveThrottleRef = useRef<{
    rafId: number | null;
    lastDelta: { deltaX: number; deltaY: number } | null;
  }>({ rafId: null, lastDelta: null });
  const [clipboardTable, setClipboardTable] = useState<TableConfig | null>(null);
  const selectedTableIdsRef = useRef(selectedTableIds);
  const clipboardTableRef = useRef(clipboardTable);
  const dragStartPositionsRef = useRef(dragStartPositions);
  selectedTableIdsRef.current = selectedTableIds;
  clipboardTableRef.current = clipboardTable;
  dragStartPositionsRef.current = dragStartPositions;

  const tableGroupRefs = useRef<Map<string, KonvaGroupShim>>(new Map());
  const registerTableGroup = useCallback((id: string, node: KonvaGroupShim | null) => {
    if (node) {
      tableGroupRefs.current.set(id, node);
    } else {
      tableGroupRefs.current.delete(id);
    }
  }, []);

  const [draggedGuest, setDraggedGuest] = useState<RSVP | null>(null);
  const [draggedSeat, setDraggedSeat] = useState<{ tableId: string; seatNumber: number; guestId: string } | null>(null);
  const draggedSeatRef = useRef<{ tableId: string; seatNumber: number; guestId: string } | null>(null);
  const [isDraggingSeat, setIsDraggingSeat] = useState(false);
  const [dragPreview, setDragPreview] = useState<{
    guestName: string;
    fill: string;
    stroke: string;
  } | null>(null);
  const dragPreviewElementRef = useRef<HTMLDivElement | null>(null);
  const dragPreviewMetaRef = useRef<{
    guestName: string;
    fill: string;
    stroke: string;
  } | null>(null);
  const [isOverUnassignZone, setIsOverUnassignZone] = useState(false);
  const dragPreviewRafRef = useRef<number | null>(null);
  const seatDragPointerListenersRef = useRef<{
    move: (e: PointerEvent) => void;
    up: (e: PointerEvent) => void;
  } | null>(null);
  const pendingDragPreviewRef = useRef<{
    x: number;
    y: number;
    guestName: string;
    fill: string;
    stroke: string;
  } | null>(null);
  const lastUnassignZoneRef = useRef(false);
  const [highlightedRelatedGuests, setHighlightedRelatedGuests] = useState<Set<string>>(new Set());
  const justDroppedRef = useRef<{ tableId: string; seatNumber: number; timestamp: number } | null>(null);
  const updateUnassignZone = useCallback((nextValue: boolean) => {
    if (lastUnassignZoneRef.current !== nextValue) {
      lastUnassignZoneRef.current = nextValue;
      setIsOverUnassignZone(nextValue);
    }
  }, []);
  const queueDragPreviewUpdate = useCallback(
    (preview: {
      x: number;
      y: number;
      guestName: string;
      fill: string;
      stroke: string;
    }) => {
    pendingDragPreviewRef.current = preview;
    if (dragPreviewRafRef.current === null) {
      dragPreviewRafRef.current = requestAnimationFrame(() => {
        dragPreviewRafRef.current = null;
        if (pendingDragPreviewRef.current) {
          const next = pendingDragPreviewRef.current;
          const nextMeta = {
            guestName: next.guestName,
            fill: next.fill,
            stroke: next.stroke,
          };
          const currentMeta = dragPreviewMetaRef.current;
          if (
            !currentMeta ||
            currentMeta.guestName !== nextMeta.guestName ||
            currentMeta.fill !== nextMeta.fill ||
            currentMeta.stroke !== nextMeta.stroke
          ) {
            dragPreviewMetaRef.current = nextMeta;
            setDragPreview(nextMeta);
          }

          const el = dragPreviewElementRef.current;
          if (el) {
            el.style.left = `${next.x - 24}px`;
            el.style.top = `${next.y - 24}px`;
          }
        }
      });
    }
  },
  []);
  const clearSeatDragPointerListeners = useCallback(() => {
    if (!seatDragPointerListenersRef.current) {
      return;
    }
    const { move, up } = seatDragPointerListenersRef.current;
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
    window.removeEventListener('pointercancel', up);
    seatDragPointerListenersRef.current = null;
  }, []);
  const resetSeatDragState = useCallback(() => {
    clearSeatDragPointerListeners();
    if (dragPreviewRafRef.current !== null) {
      cancelAnimationFrame(dragPreviewRafRef.current);
      dragPreviewRafRef.current = null;
    }
    pendingDragPreviewRef.current = null;
    draggedSeatRef.current = null;
    dragPreviewMetaRef.current = null;
    setDraggedSeat(null);
    setIsDraggingSeat(false);
    setDragPreview(null);
    lastUnassignZoneRef.current = false;
    setIsOverUnassignZone(false);
    setHighlightedRelatedGuests(new Set());
  }, [clearSeatDragPointerListeners]);

  // Modals
  const [configModalTable, setConfigModalTable] = useState<TableConfig | null>(null);
  const [guestAssignmentModal, setGuestAssignmentModal] = useState<{
    tableId: string;
    tableName: string;
    seatNumber: number;
    currentGuest: RSVP | null;
  } | null>(null);
  const [showPDFExportModal, setShowPDFExportModal] = useState(false);
  const [showAutoPopulateModal, setShowAutoPopulateModal] = useState(false);
  
  // Floor plan designs (stored as `rooms` in Firestore for backwards compatibility)
  const [rooms, setRooms] = useState<{ id: string; name: string; createdAt?: string }[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [showCreateRoomModal, setShowCreateRoomModal] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [renameTarget, setRenameTarget] = useState<{ id: string; name: string } | null>(null);
  const [allRoomTables, setAllRoomTables] = useState<Map<string, TableConfig[]>>(new Map());

  // Grid settings
  const [showGrid, setShowGrid] = useState(true);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [showRankBorders, setShowRankBorders] = useState(true);
  const [showGuestRelationships, setShowGuestRelationships] = useState(true);
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [tablePlanVersions, setTablePlanVersions] = useState<TablePlanVersionEntry[]>([]);
  const [versionsAccessDenied, setVersionsAccessDenied] = useState(false);

  /** Previous table states for Undo (table edits only; pan/zoom is not undoable). */
  const undoHistoryRef = useRef<TableConfig[][]>([]);
  const previousTablesRef = useRef<TableConfig[] | null>(null);
  const lastTablesSignatureRef = useRef<string>('');
  const lastVersionSnapshotSignatureRef = useRef<string>('');
  const lastVersionCreatedAtMsRef = useRef<number>(0);

  const refreshLiveSyncStatus = useCallback(() => {
    const { rooms, currentRoom, allRooms } = liveSyncSourcesRef.current;
    if (rooms && currentRoom && allRooms) {
      setLiveSyncStatus('live');
      return;
    }
    if (rooms || currentRoom || allRooms) {
      setLiveSyncStatus('degraded');
      return;
    }
    setLiveSyncStatus('connecting');
  }, []);

  const getTablesSignature = useCallback((inputTables: TableConfig[]): string => {
    return JSON.stringify(inputTables);
  }, []);

  const versionsCollection = useCallback(
    (roomId: string) =>
      collection(db, 'units', unitId!, 'events', eventId!, 'tablePlans', roomId, 'versions'),
    [eventId, unitId]
  );

  /** Deletes the oldest versions beyond {@link MAX_TABLE_PLAN_VERSIONS} (a few per call). */
  const pruneTablePlanVersions = useCallback(
    async (roomId: string) => {
      const versionsRef = versionsCollection(roomId);
      const countSnap = await getCountFromServer(versionsRef);
      const excess = countSnap.data().count - MAX_TABLE_PLAN_VERSIONS;
      if (excess <= 0) return;
      const oldest = await getDocs(query(versionsRef, orderBy('createdAt', 'asc'), limit(Math.min(excess, 25))));
      const batch = writeBatch(db);
      oldest.docs.forEach((d) => batch.delete(d.ref));
      await batch.commit();
    },
    [versionsCollection]
  );

  const createVersionSnapshot = useCallback(
    async (roomId: string, snapshotTables: TableConfig[], reason: VersionReason) => {
      if (!unitId || !eventId || !roomId) return;

      const signature = `${roomId}:${getTablesSignature(snapshotTables)}`;
      const nowMs = Date.now();
      const isDuplicate = signature === lastVersionSnapshotSignatureRef.current;
      const tooSoon = nowMs - lastVersionCreatedAtMsRef.current < 30000;
      if (reason === 'autosave' && (isDuplicate || tooSoon)) {
        return;
      }

      try {
        await addDoc(
          versionsCollection(roomId),
          sanitizeForFirestore({
            tables: snapshotTables,
            viewport: {
              scale: scaleRef.current,
              position: stagePosRef.current,
            },
            createdAt: new Date().toISOString(),
            lastUpdated: new Date().toISOString(),
            reason,
            createdByUid: userProfile?.uid ?? null,
          })
        );
      } catch (error: any) {
        if (error?.code === 'permission-denied') {
          adminWarn('Version snapshot skipped: no permission for versions subcollection');
          return;
        }
        throw error;
      }

      lastVersionSnapshotSignatureRef.current = signature;
      lastVersionCreatedAtMsRef.current = nowMs;
      void pruneTablePlanVersions(roomId).catch((error) => {
        adminWarn('Pruning old table plan versions failed', error);
      });
    },
    [eventId, getTablesSignature, pruneTablePlanVersions, unitId, userProfile?.uid, versionsCollection]
  );
  const gridSize = 100; // 1 meter = 100cm

  /**
   * Every floor plan's tables, with the plan being edited taken from live `tables` state (the
   * `allRoomTables` cache lags behind while a table is being dragged).
   */
  const effectiveRoomTables = useMemo(() => {
    const ownerRoomId = tablesRoomIdRef.current;
    if (!ownerRoomId) return allRoomTables;
    const next = new Map(allRoomTables);
    next.set(ownerRoomId, tables);
    return next;
  }, [allRoomTables, tables]);
  const effectiveRoomTablesRef = useRef(effectiveRoomTables);
  effectiveRoomTablesRef.current = effectiveRoomTables;

  /** Where each guest is seated, across every floor plan. */
  const guestSeatAssignments = useMemo(() => {
    const assignments = new Map<
      string,
      { roomId: string; roomName: string; roomIndex: number; tableName: string; seatNumber: number }
    >();
    const roomOrder = new Map(rooms.map((room, index) => [room.id, index]));
    const roomNames = new Map(rooms.map((room) => [room.id, room.name]));

    effectiveRoomTables.forEach((roomTables, roomId) => {
      roomTables.forEach((table) => {
        table.seating?.forEach((seat) => {
          if (seat.rsvpId && !assignments.has(seat.rsvpId)) {
            assignments.set(seat.rsvpId, {
              roomId,
              roomName: roomNames.get(roomId) ?? 'Another plan',
              roomIndex: roomOrder.get(roomId) ?? Number.MAX_SAFE_INTEGER,
              tableName: table.name,
              seatNumber: seat.seatNumber,
            });
          }
        });
      });
    });

    return assignments;
  }, [effectiveRoomTables, rooms]);

  /** Guests seated on this plan first (by seat), then other plans, then unassigned (by name). */
  const sortedGuests = useMemo(() => {
    const currentRoomId = tablesRoomIdRef.current;
    const rank = (guest: RSVP) => {
      const info = guest.id ? guestSeatAssignments.get(guest.id) : undefined;
      if (!info) return [2, 0, 0] as const;
      if (info.roomId === currentRoomId) return [0, 0, info.seatNumber] as const;
      return [1, info.roomIndex, info.seatNumber] as const;
    };
    return [...allGuests].sort((a, b) => {
      const ra = rank(a);
      const rb = rank(b);
      for (let i = 0; i < ra.length; i++) {
        if (ra[i] !== rb[i]) return ra[i] - rb[i];
      }
      return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
    });
  }, [allGuests, guestSeatAssignments]);

  const guestSeatColourMap = useMemo(
    () => buildGuestPartyColourMap(allGuests, relationshipRootPaletteIndexMap),
    [allGuests, relationshipRootPaletteIndexMap]
  );

  const { prefixBorderByRsvpId, prefixBorderLegend } = useMemo(() => {
    const { byRsvpId, legend } = buildPrefixBorderColourMap(allGuests);
    return { prefixBorderByRsvpId: byRsvpId, prefixBorderLegend: legend };
  }, [allGuests]);

  const seatDragSourceForPixi = useMemo((): { tableId: string; seatNumber: number } | null => {
    if (!draggedSeat) {
      return null;
    }
    return { tableId: draggedSeat.tableId, seatNumber: draggedSeat.seatNumber };
  }, [draggedSeat?.tableId, draggedSeat?.seatNumber]);

  const normalisePaletteIndex = useCallback((value: number): number => {
    const P = RELATIONSHIP_PARTY_PALETTE.length;
    if (!Number.isFinite(value) || P <= 0) {
      return 0;
    }
    const i = Math.floor(value);
    return ((i % P) + P) % P;
  }, []);

  const buildUniqueRelationshipPaletteIndexMap = useCallback(
    (roots: string[], existing: Map<string, number>): Map<string, number> => {
      const P = RELATIONSHIP_PARTY_PALETTE.length;
      const next = new Map<string, number>();
      const usedIndices = new Set<number>();

      for (const root of roots) {
        const existingIdx = existing.get(root);
        if (typeof existingIdx === 'number' && Number.isFinite(existingIdx)) {
          const idx = normalisePaletteIndex(existingIdx);
          if (!usedIndices.has(idx)) {
            next.set(root, idx);
            usedIndices.add(idx);
          }
        }
      }

      for (const root of roots) {
        if (next.has(root)) {
          continue;
        }
        let idx = normalisePaletteIndex(
          root.split('').reduce((acc, ch) => ((acc * 31) + ch.charCodeAt(0)) >>> 0, 0)
        );
        let attempts = 0;
        while (usedIndices.has(idx) && attempts < P) {
          idx = (idx + 1) % P;
          attempts += 1;
        }
        next.set(root, idx);
        if (attempts < P) {
          usedIndices.add(idx);
        }
      }

      return next;
    },
    [normalisePaletteIndex]
  );

  const areNumberMapsEqual = useCallback((a: Map<string, number>, b: Map<string, number>) => {
    if (a.size !== b.size) {
      return false;
    }
    for (const [key, value] of a) {
      if (b.get(key) !== value) {
        return false;
      }
    }
    return true;
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const reserveSidebar = window.innerWidth >= TABLE_PLANNER_SIDEBAR_LAYOUT_MIN_PX;
      setStageWidth(window.innerWidth - (reserveSidebar ? 400 : 100));
      setStageHeight(window.innerHeight - 200);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    return () => {
      clearSeatDragPointerListeners();
      if (dragPreviewRafRef.current !== null) {
        cancelAnimationFrame(dragPreviewRafRef.current);
      }
    };
  }, [clearSeatDragPointerListeners]);

  // Lock document to viewport so there is no page scroll (only guest list scrolls)
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prevHtml = { height: html.style.height, overflow: html.style.overflow };
    const prevBody = { height: body.style.height, overflow: body.style.overflow, margin: body.style.margin };
    const prevRoot = root ? { height: (root as HTMLElement).style.height, overflow: (root as HTMLElement).style.overflow } : null;
    html.style.height = '100%';
    html.style.overflow = 'hidden';
    body.style.height = '100%';
    body.style.overflow = 'hidden';
    body.style.margin = '0';
    if (root) {
      (root as HTMLElement).style.height = '100%';
      (root as HTMLElement).style.overflow = 'hidden';
    }
    return () => {
      html.style.height = prevHtml.height;
      html.style.overflow = prevHtml.overflow;
      body.style.height = prevBody.height;
      body.style.overflow = prevBody.overflow;
      body.style.margin = prevBody.margin;
      if (root && prevRoot) {
        (root as HTMLElement).style.height = prevRoot.height;
        (root as HTMLElement).style.overflow = prevRoot.overflow;
      }
    };
  }, []);

  // Live guest list: new RSVPs and cancellations show up without reloading the planner.
  useEffect(() => {
    if (!unitId || !eventId) {
      setLoading(false);
      return;
    }

    let receivedFirstSnapshot = false;
    // Don't hold the whole planner behind a slow guest query.
    const loadingFallback = window.setTimeout(() => {
      if (!receivedFirstSnapshot) setLoading(false);
    }, 10000);

    const unsubscribe = onSnapshot(
      collection(db, 'units', unitId, 'events', eventId, 'rsvps'),
      { includeMetadataChanges: true },
      (rsvpsSnapshot) => {
        receivedFirstSnapshot = true;
        const rsvps: RSVP[] = [];

        rsvpsSnapshot.forEach((docSnap) => {
          const data = docSnap.data() as any;
          // Skip soft-deleted (deleteRsvpSafe), cancelled, declined and apology RSVPs.
          if (!isSeatableRsvpData(data)) {
            return;
          }

          rsvps.push({
            id: docSnap.id,
            eventId,
            userId: data.userId,
            name: data.name,
            firstName: data.firstName,
            lastName: data.lastName,
            rank: data.rank,
            rankShortName: data.rankShortName,
            additionalRank: data.additionalRank,
            unit: data.unit,
            email: data.email,
            tag: data.tag || null,
            responses: data.responses,
            status: data.status,
            invoiceId: data.invoiceId,
            paid: data.paid,
            paymentStatus: data.paymentStatus,
            paymentMethod: data.paymentMethod,
            invitedBy: data.invitedBy,
            checkedIn: data.checkedIn,
            checkInTime: data.checkInTime?.toDate?.(),
            specialRequests: data.specialRequests,
            plusOneAllowed: data.plusOneAllowed,
            createdAt: data.createdAt?.toDate?.() || new Date(),
            updatedAt: data.updatedAt?.toDate?.() || new Date(),
            apologies: data.apologies === true,
          });
        });

        setRsvpGuests(rsvps);
        if (!rsvpsSnapshot.metadata.fromCache) {
          setGuestsLoadedFromServer(true);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching guests:', error);
        showError('Failed to load guests. You can still use the planner and refresh guest data shortly.');
        setLoading(false);
      }
    );

    return () => {
      window.clearTimeout(loadingFallback);
      unsubscribe();
    };
  }, [unitId, eventId]);

  // Ticket mode decides whether multi-ticket bookings get "Guest of …" placeholders.
  useEffect(() => {
    if (!unitId || !eventId) return;
    return onSnapshot(
      doc(db, 'units', unitId, 'events', eventId),
      (snapshot) => setIsMultiTenant(isMultiTenantEvent(snapshot.data() as Pick<DiningEvent, 'ticketMode'> | undefined)),
      (error) => {
        console.error('Error fetching event:', error);
        setIsMultiTenant(false);
      }
    );
  }, [unitId, eventId]);

  // Listen for persisted relationship colours so they remain stable across sessions/devices.
  useEffect(() => {
    if (!unitId || !eventId) {
      setRelationshipRootPaletteIndexMap(new Map());
      return;
    }

    const colourDocRef = doc(
      db,
      'units',
      unitId,
      'events',
      eventId,
      'tablePlan',
      'relationshipColours'
    );

    const unsubscribe = onSnapshot(
      colourDocRef,
      (snapshot) => {
        if (snapshot.metadata.hasPendingWrites) {
          return;
        }

        const data = snapshot.data();
        const version = data?.colourSchemeVersion;
        const rawMap = data?.rootHueById;
        const next = new Map<string, number>();
        const P = RELATIONSHIP_PARTY_PALETTE.length;

        if (
          version === RELATIONSHIP_PARTY_COLOUR_SCHEME_VERSION &&
          rawMap &&
          typeof rawMap === 'object'
        ) {
          for (const [root, idx] of Object.entries(rawMap)) {
            if (typeof idx === 'number' && Number.isFinite(idx)) {
              const n = Math.floor(idx);
              if (n >= 0 && n < P) {
                next.set(root, n);
              }
            }
          }
        }
        setRelationshipRootPaletteIndexMap(next);
      },
      (error) => {
        console.error('Error listening to relationship colours:', error);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [unitId, eventId]);

  // Ensure every relationship group has a unique persisted colour assignment.
  useEffect(() => {
    if (!unitId || !eventId) {
      return;
    }

    const roots = collectGuestRelationshipRoots(allGuests);
    const desiredMap = buildUniqueRelationshipPaletteIndexMap(
      roots,
      relationshipRootPaletteIndexMap
    );

    if (areNumberMapsEqual(desiredMap, relationshipRootPaletteIndexMap)) {
      return;
    }

    setRelationshipRootPaletteIndexMap(desiredMap);
    const colourDocRef = doc(
      db,
      'units',
      unitId,
      'events',
      eventId,
      'tablePlan',
      'relationshipColours'
    );

    const serialised: Record<string, number> = {};
    desiredMap.forEach((paletteIndex, root) => {
      serialised[root] = paletteIndex;
    });

    void setDoc(
      colourDocRef,
      sanitizeForFirestore({
        colourSchemeVersion: RELATIONSHIP_PARTY_COLOUR_SCHEME_VERSION,
        rootHueById: serialised,
        updatedAt: new Date().toISOString(),
      }),
      { merge: true }
    ).catch((error) => {
      console.error('Failed to persist relationship colours:', error);
    });
  }, [
    unitId,
    eventId,
    allGuests,
    relationshipRootPaletteIndexMap,
    buildUniqueRelationshipPaletteIndexMap,
    areNumberMapsEqual,
  ]);

  // Room functions
  const createRoom = async () => {
    if (!newRoomName.trim() || !unitId || !eventId) return;
    
    try {
      const roomData = {
        name: newRoomName.trim(),
        createdAt: new Date().toISOString()
      };
      
      // Sanitize data to remove undefined values
      const sanitizedRoomData = sanitizeForFirestore(roomData);
      
      const roomRef = await addDoc(
        collection(db, 'units', unitId, 'events', eventId, 'rooms'),
        sanitizedRoomData
      );

      // Save the plan being left before the new (empty) one replaces it on screen.
      cancelPendingViewportPersist();
      persistViewport();
      await flushTablePlanSave();
      setSelectedRoomId(roomRef.id);
      setNewRoomName('');
      setShowCreateRoomModal(false);

      showSuccess('Floor plan created');
    } catch (error) {
      console.error('Error creating room:', error);
      showError('Failed to create floor plan');
    }
  };

  const renameDesign = async () => {
    if (!renameTarget?.name.trim() || !unitId || !eventId) return;
    const name = renameTarget.name.trim();
    try {
      await updateDoc(
        doc(db, 'units', unitId, 'events', eventId, 'rooms', renameTarget.id),
        sanitizeForFirestore({ name })
      );
      setRooms((prev) =>
        prev.map((r) => (r.id === renameTarget.id ? { ...r, name } : r))
      );
      setRenameTarget(null);
      showSuccess('Floor plan renamed');
    } catch (error) {
      console.error('Error renaming floor plan:', error);
      showError('Failed to rename floor plan');
    }
  };

  /** Floor plans being removed: their docs must not be re-created by a late save or listener. */
  const deletingRoomIdsRef = useRef<Set<string>>(new Set());

  const deleteRoomVersions = async (roomId: string) => {
    const snapshot = await getDocs(versionsCollection(roomId));
    for (let i = 0; i < snapshot.docs.length; i += 400) {
      const batch = writeBatch(db);
      snapshot.docs.slice(i, i + 400).forEach((d) => batch.delete(d.ref));
      await batch.commit();
    }
  };

  const deleteDesign = async (roomId: string) => {
    if (!unitId || !eventId) return;
    const design = rooms.find((r) => r.id === roomId);
    const label = design?.name ?? 'this floor plan';
    const result = await confirmAction({
      title: 'Remove floor plan?',
      text: `Remove "${label}"? Tables and seating for this design will be permanently removed.`,
      confirmButtonText: 'Remove',
      icon: 'warning',
    });
    if (!result.isConfirmed) return;

    const remaining = rooms.filter((r) => r.id !== roomId);
    deletingRoomIdsRef.current.add(roomId);

    try {
      try {
        await deleteRoomVersions(roomId);
      } catch (error: any) {
        // Version history is best-effort; don't block removing the plan itself.
        if (error?.code !== 'permission-denied') {
          console.error('Error removing floor plan versions:', error);
        }
      }
      await deleteDoc(doc(db, 'units', unitId, 'events', eventId, 'tablePlans', roomId));
      await deleteDoc(doc(db, 'units', unitId, 'events', eventId, 'rooms', roomId));

      if (tablesRoomIdRef.current === roomId) {
        tablesRoomIdRef.current = null;
        tablesRef.current = [];
        setTables([]);
        setSelectedTableIds(new Set());
      }
      if (selectedRoomId === roomId) {
        setSelectedRoomId(remaining.length > 0 ? remaining[0].id : null);
      }
      setRooms(remaining);
      setAllRoomTables((prev) => {
        const next = new Map(prev);
        next.delete(roomId);
        return next;
      });

      showSuccess('Floor plan removed');
      if (remaining.length === 0) {
        setShowCreateRoomModal(true);
      }
    } catch (error) {
      deletingRoomIdsRef.current.delete(roomId);
      console.error('Error removing floor plan:', error);
      showError('Failed to remove floor plan');
    }
  };

  /**
   * Saves the loaded floor plan's tables if they changed. Always writes to the plan the tables
   * belong to (`tablesRoomIdRef`), never to whichever plan is selected by the time it runs.
   */
  const autoSaveTablePlan = async (versionReason?: VersionReason) => {
    const roomId = tablesRoomIdRef.current;
    if (!unitId || !eventId || !roomId || deletingRoomIdsRef.current.has(roomId)) {
      return;
    }
    if (versionReason) {
      pendingVersionReasonRef.current = versionReason;
    }

    if (autoSaveInFlightRef.current) {
      pendingAutoSaveRef.current = true;
      return;
    }

    const tablesToSave = tablesRef.current;
    const tablesSignature = getTablesSignature(tablesToSave);
    if (tablesSignature === lastSavedTablesSignatureRef.current) {
      pendingVersionReasonRef.current = null;
      return;
    }

    const reason = pendingVersionReasonRef.current ?? 'autosave';
    pendingVersionReasonRef.current = null;
    autoSaveInFlightRef.current = true;
    let markDone: () => void = () => {};
    autoSaveDoneRef.current = new Promise<void>((resolve) => {
      markDone = resolve;
    });
    setAutoSaving(true);

    try {
      await setDoc(
        doc(db, 'units', unitId, 'events', eventId, 'tablePlans', roomId),
        sanitizeForFirestore({ tables: tablesToSave, lastUpdated: new Date().toISOString() }),
        { merge: true }
      );

      if (tablesRoomIdRef.current === roomId) {
        lastSavedTablesRef.current = tablesToSave;
        lastSavedTablesSignatureRef.current = tablesSignature;
      }
      setLastSaved(new Date());
      await createVersionSnapshot(roomId, tablesToSave, reason);
    } catch (error: any) {
      console.error('Error auto-saving table plan:', error);
      showError(`Failed to auto-save: ${error?.message || 'Unknown error'}`);
    } finally {
      autoSaveInFlightRef.current = false;
      if (pendingAutoSaveRef.current) {
        pendingAutoSaveRef.current = false;
        // Starts synchronously, so anyone awaiting `autoSaveDoneRef` below sees the follow-up save.
        void autoSaveTablePlanRef.current();
      } else {
        setAutoSaving(false);
      }
      markDone();
    }
  };

  /** Waits for any save in progress, then saves outstanding edits to the loaded plan. */
  const flushTablePlanSave = async () => {
    while (autoSaveInFlightRef.current) {
      await autoSaveDoneRef.current;
    }
    await autoSaveTablePlanRef.current();
  };
  const autoSaveTablePlanRef = useRef(autoSaveTablePlan);
  autoSaveTablePlanRef.current = autoSaveTablePlan;

  const cancelPendingViewportPersist = () => {
    if (viewportPersistTimeoutRef.current !== null) {
      window.clearTimeout(viewportPersistTimeoutRef.current);
      viewportPersistTimeoutRef.current = null;
    }
  };

  /** Remembers the current pan/zoom for the loaded plan in this browser. */
  const persistViewport = useCallback(() => {
    const roomId = viewportRoomIdRef.current;
    if (!unitId || !eventId || !roomId) return;
    writeStoredViewport(viewportStorageKey(unitId, eventId, roomId), {
      scale: scaleRef.current,
      position: stagePosRef.current,
    });
  }, [unitId, eventId]);

  /**
   * Debounced after zoom / wheel / toolbar view actions.
   * Stage pan does not use this until the pointer is released (see onViewportChange).
   */
  const scheduleViewportPersist = (delayMs = 600) => {
    cancelPendingViewportPersist();
    viewportPersistTimeoutRef.current = window.setTimeout(() => {
      viewportPersistTimeoutRef.current = null;
      if (isViewportPanningRef.current) {
        return;
      }
      persistViewport();
    }, delayMs);
  };

  useEffect(() => {
    return () => {
      if (viewportPersistTimeoutRef.current !== null) {
        window.clearTimeout(viewportPersistTimeoutRef.current);
        persistViewport();
      }
    };
  }, [persistViewport]);

  // Load rooms on component mount
  useEffect(() => {
    liveSyncSourcesRef.current = {
      rooms: false,
      currentRoom: false,
      allRooms: false,
    };
    refreshLiveSyncStatus();

    if (!unitId || !eventId) {
      return;
    }

    const roomsRef = collection(db, 'units', unitId, 'events', eventId, 'rooms');
    const unsubscribe = onSnapshot(
      roomsRef,
      (roomsSnapshot) => {
        const roomsList: { id: string; name: string; createdAt?: string }[] = [];
        roomsSnapshot.forEach((docSnap) => {
          if (deletingRoomIdsRef.current.has(docSnap.id)) return;
          const data = docSnap.data();
          const createdRaw = data.createdAt;
          const createdAt =
            typeof createdRaw === 'string'
              ? createdRaw
              : createdRaw?.toDate?.()?.toISOString?.() ?? '';

          roomsList.push({
            id: docSnap.id,
            name: data.name,
            createdAt,
          });
        });

        roomsList.sort((a, b) => {
          const ca = a.createdAt || '';
          const cb = b.createdAt || '';
          if (ca && cb) return ca.localeCompare(cb);
          return a.name.localeCompare(b.name);
        });

        setRooms(roomsList);
        setLoading(false);
        setSelectedRoomId((current) => {
          if (roomsList.length === 0) {
            return null;
          }
          if (current && roomsList.some((r) => r.id === current)) {
            return current;
          }
          return roomsList[0].id;
        });

        setShowCreateRoomModal(roomsList.length === 0);
        liveSyncSourcesRef.current.rooms = true;
        refreshLiveSyncStatus();
      },
      (error) => {
        console.error('Error listening to rooms:', error);
        liveSyncSourcesRef.current.rooms = false;
        refreshLiveSyncStatus();
        showError('Failed to sync floor plans in real time');
      }
    );

    return () => {
      liveSyncSourcesRef.current.rooms = false;
      refreshLiveSyncStatus();
      unsubscribe();
    };
  }, [unitId, eventId, refreshLiveSyncStatus]);

  // Load the selected floor plan and keep it in sync with other editors.
  useEffect(() => {
    if (!unitId || !eventId || !selectedRoomId) {
      liveSyncSourcesRef.current.currentRoom = !!(unitId && eventId && !selectedRoomId);
      refreshLiveSyncStatus();
      return;
    }

    const roomId = selectedRoomId;
    const tablePlanRef = doc(db, 'units', unitId, 'events', eventId, 'tablePlans', roomId);
    const unsubscribe = onSnapshot(
      tablePlanRef,
      (snapshot) => {
        if (snapshot.metadata.hasPendingWrites || deletingRoomIdsRef.current.has(roomId)) {
          return;
        }
        liveSyncSourcesRef.current.currentRoom = true;
        refreshLiveSyncStatus();

        const data = snapshot.exists() ? snapshot.data() : null;
        const remoteTables = normaliseRoomTables(data?.tables || []);
        const remoteSignature = getTablesSignature(remoteTables);

        if (tablesRoomIdRef.current !== roomId) {
          // First snapshot for this plan: load it, reset undo, restore this browser's view of it.
          tablesRoomIdRef.current = roomId;
          lastSavedTablesRef.current = remoteTables;
          lastSavedTablesSignatureRef.current = remoteSignature;
          undoHistoryRef.current = [];
          previousTablesRef.current = remoteTables;
          lastTablesSignatureRef.current = remoteSignature;
          ignoreHistorySignatureRef.current = null;
          tablesRef.current = remoteTables;
          setTables(remoteTables);
          setSelectedTableIds(new Set());

          const stored = readStoredViewport(viewportStorageKey(unitId, eventId, roomId));
          const legacy = data?.viewport;
          const viewport: PlannerViewport = stored ?? {
            scale: Number(legacy?.scale) > 0 ? Number(legacy.scale) : 1,
            position: legacy?.position ?? { x: 0, y: 0 },
          };
          viewportRoomIdRef.current = roomId;
          scaleRef.current = viewport.scale;
          stagePosRef.current = viewport.position;
          setScale(viewport.scale);
          setStagePos(viewport.position);
          return;
        }

        // Another editor changed this plan. Keep our unsaved edits to other tables (per-table merge).
        const base = lastSavedTablesRef.current;
        const local = tablesRef.current;
        lastSavedTablesRef.current = remoteTables;
        lastSavedTablesSignatureRef.current = remoteSignature;

        const hasLocalEdits = getTablesSignature(local) !== getTablesSignature(base);
        const next = hasLocalEdits
          ? recalculateSeatNumbers(mergeTables(base, local, remoteTables))
          : remoteTables;
        const nextSignature = getTablesSignature(next);
        if (nextSignature === getTablesSignature(local)) {
          return;
        }

        // Undoing past someone else's change would silently revert it, so start undo afresh.
        undoHistoryRef.current = [];
        ignoreHistorySignatureRef.current = nextSignature;
        tablesRef.current = next;
        setTables(next);
      },
      (error) => {
        console.error('Error listening to table plan:', error);
        liveSyncSourcesRef.current.currentRoom = false;
        refreshLiveSyncStatus();
      }
    );

    return () => {
      liveSyncSourcesRef.current.currentRoom = false;
      refreshLiveSyncStatus();
      unsubscribe();
    };
  }, [unitId, eventId, selectedRoomId, refreshLiveSyncStatus, getTablesSignature]);

  // Keep every floor plan's tables cached (guest list, cross-plan seating, PDF export).
  useEffect(() => {
    if (!unitId || !eventId) {
      liveSyncSourcesRef.current.allRooms = false;
      refreshLiveSyncStatus();
      return;
    }

    if (rooms.length === 0) {
      liveSyncSourcesRef.current.allRooms = true;
      refreshLiveSyncStatus();
      return;
    }

    const unsubscribes = rooms.map((room) => {
      const roomPlanRef = doc(db, 'units', unitId, 'events', eventId, 'tablePlans', room.id);
      return onSnapshot(
        roomPlanRef,
        (snapshot) => {
          if (snapshot.metadata.hasPendingWrites || deletingRoomIdsRef.current.has(room.id)) {
            return;
          }
          liveSyncSourcesRef.current.allRooms = true;
          refreshLiveSyncStatus();

          const roomTables = snapshot.exists()
            ? normaliseRoomTables(snapshot.data().tables || [])
            : [];

          setAllRoomTables((prev) => {
            const next = new Map(prev);
            next.set(room.id, roomTables);
            return next;
          });
        },
        (error) => {
          console.error(`Error listening to table plan for room ${room.id}:`, error);
          liveSyncSourcesRef.current.allRooms = false;
          refreshLiveSyncStatus();
        }
      );
    });

    return () => {
      liveSyncSourcesRef.current.allRooms = false;
      refreshLiveSyncStatus();
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [unitId, eventId, rooms, refreshLiveSyncStatus]);

  // Undo tracking: remember the previous tables whenever the user changes them.
  useEffect(() => {
    if (!tablesRoomIdRef.current) {
      return;
    }
    const signature = getTablesSignature(tables);
    if (signature === lastTablesSignatureRef.current) {
      return;
    }
    if (ignoreHistorySignatureRef.current === signature) {
      ignoreHistorySignatureRef.current = null;
    } else if (previousTablesRef.current) {
      undoHistoryRef.current = [...undoHistoryRef.current, previousTablesRef.current].slice(
        -UNDO_HISTORY_LIMIT
      );
    }
    lastTablesSignatureRef.current = signature;
    previousTablesRef.current = tables;
  }, [tables, getTablesSignature]);

  // Handle design selection: flush the plan being left, remember its view, then switch.
  const handleRoomChange = async (newRoomId: string) => {
    if (newRoomId === selectedRoomId) return;
    cancelPendingViewportPersist();
    persistViewport();
    await flushTablePlanSave();
    setSelectedRoomId(newRoomId);
  };

  // Auto-save shortly after table edits (pan/zoom is stored locally, see persistViewport).
  useEffect(() => {
    if (!tablesRoomIdRef.current || loading) {
      return;
    }
    if (getTablesSignature(tables) === lastSavedTablesSignatureRef.current) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      void autoSaveTablePlanRef.current();
    }, 1000);
    return () => window.clearTimeout(timeoutId);
  }, [tables, loading, getTablesSignature]);

  const handleAddTable = (tableType: 'rectangular' | 'square' | 'circular' | 'oval' = 'rectangular') => {
    const usedNames = new Set(tables.map((t) => t.name));
    let tableNumber = tables.length + 1;
    while (usedNames.has(`Table ${tableNumber}`)) tableNumber += 1;
    const maxPriority = tables.reduce((max, t) => Math.max(max, t.seatNumberPriority ?? 0), 0);

    const newTable: TableConfig = {
      id: `table-${Date.now()}`,
      name: `Table ${tableNumber}`,
      type: tableType,
      seats: 8,
      x: TABLE_PLANNER_PLAYABLE_AREA_SIZE / 2,
      y: TABLE_PLANNER_PLAYABLE_AREA_SIZE / 2,
      rotation: 0,
      width: 150,
      height: tableType === 'rectangular' ? 80 : 150, // 0.8m height for rectangular, square for others
      color: '#ffffff',
      seatPlacement: {
        bothSides: tableType === 'rectangular' ? true : false,
        oneSide: false,
        leftEnd: false,
        rightEnd: false,
        noSeats: false,
        // Square table defaults - all sides enabled
        topSide: tableType === 'square' ? true : undefined,
        bottomSide: tableType === 'square' ? true : undefined,
        leftSide: tableType === 'square' ? true : undefined,
        rightSide: tableType === 'square' ? true : undefined,
      },
      chairColor: '#e5e7eb',
      seatSize: 28,
      seatNumberPriority: maxPriority + 1, // Numbered after every existing table
      seating: Array.from({ length: 8 }, (_, i) => ({
        seatNumber: i + 1,
        rsvpId: null,
        role: null,
        x: 0,
        y: 0,
      })),
    };

    const calculatedTable = calculateSeatingPositions(newTable);
    const updatedTables = [...tables, calculatedTable];
    const recalculatedTables = recalculateSeatNumbers(updatedTables);
    setTables(recalculatedTables);
  };

  const handleTableDragStart = useCallback((tableId: string) => {
    if (isDraggingSeat) return;
    setIsDraggingTable(true);
    
    // Store starting positions of all selected tables (or just the dragged one if not selected)
    const tablesToMove = selectedTableIds.has(tableId) ? Array.from(selectedTableIds) : [tableId];
    const startPositions = new Map<string, { x: number; y: number }>();
    
    tablesToMove.forEach(id => {
      const table = tables.find(t => t.id === id);
      if (table) {
        startPositions.set(id, { x: table.x, y: table.y });
      }
    });
    
    setDragStartPositions(startPositions);
    
    // If dragging a table that's not selected, select only that table
    if (!selectedTableIds.has(tableId)) {
      setSelectedTableIds(new Set([tableId]));
    }
  }, [isDraggingSeat, selectedTableIds, tables]);

  const handleTableDragMove = useCallback((tableId: string, x: number, y: number) => {
    if (isDraggingSeat) return;
    const starts = dragStartPositionsRef.current;
    const startPos = starts.get(tableId);
    if (!startPos) return;

    const deltaX = x - startPos.x;
    const deltaY = y - startPos.y;

    dragMoveThrottleRef.current.lastDelta = { deltaX, deltaY };
    const { rafId } = dragMoveThrottleRef.current;

    if (rafId === null) {
      dragMoveThrottleRef.current.rafId = requestAnimationFrame(() => {
        dragMoveThrottleRef.current.rafId = null;
        const pending = dragMoveThrottleRef.current.lastDelta;
        if (!pending) return;
        const map = dragStartPositionsRef.current;
        const sel = selectedTableIdsRef.current;
        const toMove = sel.has(tableId) ? Array.from(sel) : [tableId];
        toMove.forEach((id) => {
          const tableStartPos = map.get(id);
          const node = tableGroupRefs.current.get(id);
          if (tableStartPos && node) {
            node.x(tableStartPos.x + pending.deltaX);
            node.y(tableStartPos.y + pending.deltaY);
          }
        });
        tableGroupRefs.current.get(tableId)?.getLayer()?.batchDraw();
      });
    }
  }, [isDraggingSeat]);

  const handleTableDragEnd = useCallback((tableId: string, x: number, y: number) => {
    if (isDraggingSeat) return;
    const { rafId } = dragMoveThrottleRef.current;
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      dragMoveThrottleRef.current.rafId = null;
    }
    const startPositions = new Map(dragStartPositions);
    setIsDraggingTable(false);
    setDragStartPositions(new Map());

    const tablesToMove = selectedTableIds.has(tableId) ? Array.from(selectedTableIds) : [tableId];
    const primaryStart = startPositions.get(tableId);

    setTables((prevTables) => {
      let next = prevTables;
      const deltaX = primaryStart != null ? x - primaryStart.x : 0;
      const deltaY = primaryStart != null ? y - primaryStart.y : 0;
      next = prevTables.map((t) => {
        if (!tablesToMove.includes(t.id)) return t;
        const tableStart = startPositions.get(t.id);
        if (tableStart != null) {
          const newX = t.id === tableId ? x : tableStart.x + deltaX;
          const newY = t.id === tableId ? y : tableStart.y + deltaY;
          return { ...t, x: newX, y: newY };
        }
        return t;
      });
      if (snapToGrid) {
        next = next.map((t) => {
          if (tablesToMove.includes(t.id)) {
            const snappedX =
              Math.round(t.x / TABLE_PLANNER_METRIC_STEP) *
              TABLE_PLANNER_METRIC_STEP;
            const snappedY =
              Math.round(t.y / TABLE_PLANNER_METRIC_STEP) *
              TABLE_PLANNER_METRIC_STEP;
            return { ...t, x: snappedX, y: snappedY };
          }
          return t;
        });
      }
      return next;
    });
  }, [isDraggingSeat, selectedTableIds, snapToGrid, dragStartPositions]);

  const handleTableSelect = useCallback((tableId: string, event?: MouseEvent | React.MouseEvent) => {
    const isMultiSelect = event && (event.ctrlKey || event.metaKey);
    
    if (isMultiSelect) {
      // Toggle selection: add if not selected, remove if selected
      setSelectedTableIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(tableId)) {
          newSet.delete(tableId);
        } else {
          newSet.add(tableId);
        }
        return newSet;
      });
    } else {
      // Single select: replace selection
      setSelectedTableIds(new Set([tableId]));
    }
    // The canvas draws (and hit-tests) selected tables on top, so the tables array is left alone:
    // reordering it on every click caused a save + undo step and could renumber tied tables.
  }, []);

  const handleTableDoubleClick = useCallback((tableId: string) => {
    // Double-click opens configuration modal
    const table = tables.find((t) => t.id === tableId);
    if (table) {
      setConfigModalTable(table);
    }
  }, [tables]);

  const handleSaveTableConfig = (updatedTable: TableConfig) => {
    const updatedTables = tablesRef.current.map((t) => (t.id === updatedTable.id ? updatedTable : t));
    setTables(recalculateSeatNumbers(updatedTables));
    setConfigModalTable(null);
  };

  const handleUpdateTableConfig = (updatedTable: TableConfig) => {
    const updatedTables = tablesRef.current.map((t) => (t.id === updatedTable.id ? updatedTable : t));
    setTables(recalculateSeatNumbers(updatedTables));
  };

  /**
   * Applies a layout change (seat count, type, size…) to the given tables, keeping guests seated
   * wherever the new layout has room, and warns about anyone who no longer fits.
   */
  const applyTableLayoutChange = (
    tableIds: ReadonlySet<string>,
    change: (table: TableConfig) => { table: TableConfig; unseatedGuestIds: string[] }
  ) => {
    const unseated: string[] = [];
    const updatedTables = tablesRef.current.map((t) => {
      if (!tableIds.has(t.id)) return t;
      const result = change(t);
      unseated.push(...result.unseatedGuestIds);
      return result.table;
    });
    const next = recalculateSeatNumbers(updatedTables);
    tablesRef.current = next;
    setTables(next);
    if (unseated.length > 0) {
      const names = unseated.map((id) => guestNameMap.get(id) ?? 'A guest').join(', ');
      showWarning(
        `${unseated.length} guest${unseated.length > 1 ? 's' : ''} no longer fit and ${
          unseated.length > 1 ? 'were' : 'was'
        } unseated: ${names}. Use Undo to revert.`
      );
    }
  };

  const handleTableResize = useCallback((tableId: string, width: number, height: number, x: number, y: number) => {
    setTables((prevTables) =>
      prevTables.map((t) => {
        if (t.id === tableId) {
          // Recalculate seating positions when table is resized; guests stay on their side.
          return reflowTable(t, { width, height, x, y }).table;
        }
        return t;
      })
    );
  }, []);

  const adjustSideSeatCount = (
    tableId: string,
    side: 'top' | 'bottom' | 'left' | 'right' | 'leftEnd' | 'rightEnd',
    delta: number
  ) => {
    const deriveSeatCounts = (table: TableConfig) => {
      const counts: Record<string, number> = { top: 0, bottom: 0, left: 0, right: 0, leftEnd: 0, rightEnd: 0 };
      const width = table.width;
      table.seating.forEach((s) => {
        // Ends: chairs positioned beyond table width horizontally
        if (s.x <= -width / 2 - 20) counts.leftEnd++;
        else if (s.x >= width / 2 + 20) counts.rightEnd++;
        // Sides for rectangular/square: y sign indicates top/bottom; x sign for left/right on square
        else if (s.y < 0) counts.top++;
        else if (s.y > 0) counts.bottom++;
        else if (s.x < 0) counts.left++;
        else if (s.x > 0) counts.right++;
      });
      return counts;
    };

    applyTableLayoutChange(new Set([tableId]), (t) => {
      // Round tables: just adjust the total seat count
      if (t.type === 'circular' || t.type === 'oval') {
        return resizeTableSeats(t, t.seats + delta);
      }

      // Rectangular/square tables: adjust the per-side count
      const nextCounts = { ...deriveSeatCounts(t), ...t.seatCounts } as Record<string, number>;
      nextCounts[side] = Math.max(0, Math.floor((nextCounts[side] ?? 0) + delta));
      const total =
        t.type === 'rectangular'
          ? (nextCounts.top || 0) + (nextCounts.bottom || 0) + (nextCounts.leftEnd || 0) + (nextCounts.rightEnd || 0)
          : (nextCounts.top || 0) + (nextCounts.bottom || 0) + (nextCounts.left || 0) + (nextCounts.right || 0);
      return reflowTable(t, { seatCounts: nextCounts, seats: total });
    });
  };

  const handleTableRotate = useCallback((tableId: string, rotation: number) => {
    setTables((prevTables) =>
      prevTables.map((t) => (t.id === tableId ? { ...t, rotation } : t))
    );
  }, []);

  const handleDuplicateTable = () => {
    if (selectedTableIds.size === 0) return;
    
    // Duplicate the first selected table (or all selected tables if desired)
    const firstSelectedId = Array.from(selectedTableIds)[0];
    const tableToDuplicate = tables.find((t) => t.id === firstSelectedId);
    if (!tableToDuplicate) return;

    // Get the original table's priority (default to 999 if undefined)
    const originalPriority = tableToDuplicate.seatNumberPriority ?? 999;
    
    // Create a duplicate with new ID, offset position, and increased seat priority
    const duplicatedTable: TableConfig = {
      ...tableToDuplicate,
      id: `table-${Date.now()}`,
      name: `${tableToDuplicate.name} (Copy)`,
      x: tableToDuplicate.x + 50, // Offset to the right
      y: tableToDuplicate.y + 50, // Offset down
      seatNumberPriority: originalPriority + 1, // Increase priority by 1
      seating: tableToDuplicate.seating.map((seat) => ({
        ...seat,
        rsvpId: null, // Clear guest assignments
        role: null,
      })),
    };

    const calculated = calculateSeatingPositions(duplicatedTable);
    const recalculated = recalculateSeatNumbers([...tables, calculated]);
    setTables(recalculated);
    setSelectedTableIds(new Set([duplicatedTable.id])); // Select the new duplicate
  };

  // Keyboard shortcuts: Delete/Backspace, Ctrl/Cmd + C (copy), Ctrl/Cmd + V (paste), Ctrl/Cmd + Z (undo)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const active = document.activeElement as HTMLElement | null;
      // Avoid when typing, or while any dialog / confirmation is open
      if (active && (['INPUT', 'TEXTAREA', 'SELECT'].includes(active.tagName) || active.isContentEditable)) return;
      if (document.querySelector('[role="dialog"], [role="alertdialog"], .swal2-container')) return;

      // Handle Delete key
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const sel = selectedTableIdsRef.current;
        if (sel.size === 0) return;

        const tableCount = sel.size;
        const tablesNow = tablesRef.current;
        const selectedTables = tablesNow.filter((t) => sel.has(t.id));
        const tableNames = selectedTables.map((t) => t.name).join(', ');
        const seatedCount = selectedTables.reduce(
          (sum, t) => sum + t.seating.filter((s) => s.rsvpId).length,
          0
        );
        const seatedNote =
          seatedCount > 0
            ? ` ${seatedCount} seated guest${seatedCount > 1 ? 's' : ''} will be unassigned.`
            : '';

        confirmAction({
          title: `Delete ${tableCount} Table${tableCount > 1 ? 's' : ''}`,
          text: `Are you sure you want to delete ${tableCount > 1 ? 'these tables' : `"${tableNames}"`}?${seatedNote} You can use Undo to bring ${tableCount > 1 ? 'them' : 'it'} back.`,
          icon: 'warning',
        }).then((result) => {
          if (result.isConfirmed) {
            const idsToDelete = Array.from(selectedTableIdsRef.current);
            idsToDelete.forEach((id) => handleDeleteTable(id));
            setSelectedTableIds(new Set());
            showSuccess(`${tableCount} table${tableCount > 1 ? 's' : ''} deleted`);
          }
        });
        e.preventDefault();
        return;
      }

      // Handle modifier key combinations
      const isModifier = e.ctrlKey || e.metaKey;
      if (!isModifier) return;

      if (e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        void handleUndoRef.current();
        return;
      }

      if (e.key.toLowerCase() === 'c') {
        const sel = selectedTableIdsRef.current;
        if (sel.size === 0) return;
        // Let the browser copy any highlighted text instead of the table.
        if (window.getSelection()?.toString()) return;
        const firstSelectedId = Array.from(sel)[0];
        const t = tablesRef.current.find((tb) => tb.id === firstSelectedId);
        if (!t) return;
        const copy: TableConfig = JSON.parse(JSON.stringify(t));
        setClipboardTable(copy);
        showSuccess('Table copied');
        e.preventDefault();
      }

      if (e.key.toLowerCase() === 'v') {
        const clip = clipboardTableRef.current;
        if (!clip) return;
        const originalPriority = clip.seatNumberPriority ?? 999;

        const pasted: TableConfig = {
          ...clip,
          id: `table-${Date.now()}`,
          name: `${clip.name} (Copy)`,
          x: (clip.x || 0) + 40,
          y: (clip.y || 0) + 40,
          seatNumberPriority: originalPriority + 1,
          seating: clip.seating.map((s: any) => ({ ...s, rsvpId: null, role: null })),
        } as TableConfig;

        const calculated = calculateSeatingPositions(pasted);
        const recalculated = recalculateSeatNumbers([...tablesRef.current, calculated]);
        setTables(recalculated);
        setSelectedTableIds(new Set([calculated.id]));
        showSuccess('Table pasted');
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleDeleteTable = (tableId: string) => {
    setTables((prev) => {
      const updatedTables = prev.filter((t) => t.id !== tableId);
      return recalculateSeatNumbers(updatedTables);
    });
    setConfigModalTable(null);
    setSelectedTableIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(tableId);
      return newSet;
    });
  };

  const handleSeatClick = (tableId: string, seatNumber: number) => {
    // Prevent click if we just dropped on this seat (within 200ms)
    if (justDroppedRef.current) {
      const { tableId: droppedTableId, seatNumber: droppedSeatNumber, timestamp } = justDroppedRef.current;
      const timeSinceDrop = Date.now() - timestamp;
      if (
        droppedTableId === tableId &&
        droppedSeatNumber === seatNumber &&
        timeSinceDrop < 200
      ) {
        // Clear the ref and ignore this click
        justDroppedRef.current = null;
        return;
      }
      // Clear old refs
      if (timeSinceDrop >= 200) {
        justDroppedRef.current = null;
      }
    }

    const table = tables.find((t) => t.id === tableId);
    if (!table) return;

    const seat = table.seating.find((s) => s.seatNumber === seatNumber);
    if (!seat) return;

    const guest = seat.rsvpId ? allGuests.find((g) => g.id === seat.rsvpId) || null : null;
    setGuestAssignmentModal({
      tableId,
      tableName: table.name,
      seatNumber,
      currentGuest: guest,
    });
  };

  /**
   * A guest can only hold one seat across all floor plans. Clears the given guests from every plan
   * other than the one being edited: the cache updates immediately and each affected plan is
   * updated in a transaction, so a concurrent edit to that plan isn't overwritten.
   */
  const removeGuestsFromOtherPlans = (guestIds: ReadonlySet<string>) => {
    if (!unitId || !eventId || guestIds.size === 0) return;
    const ownerRoomId = tablesRoomIdRef.current;
    const affectedRoomIds: string[] = [];
    effectiveRoomTablesRef.current.forEach((roomTables, roomId) => {
      if (roomId !== ownerRoomId && removeGuestsFromTables(roomTables, guestIds).changed) {
        affectedRoomIds.push(roomId);
      }
    });
    if (affectedRoomIds.length === 0) return;

    setAllRoomTables((prev) => {
      const next = new Map(prev);
      for (const roomId of affectedRoomIds) {
        const roomTables = prev.get(roomId);
        if (roomTables) next.set(roomId, removeGuestsFromTables(roomTables, guestIds).tables);
      }
      return next;
    });

    for (const roomId of affectedRoomIds) {
      const planRef = doc(db, 'units', unitId, 'events', eventId, 'tablePlans', roomId);
      void runTransaction(db, async (transaction) => {
        const snap = await transaction.get(planRef);
        if (!snap.exists()) return;
        const rawTables = Array.isArray(snap.data().tables) ? (snap.data().tables as TableConfig[]) : [];
        const result = removeGuestsFromTables(rawTables, guestIds);
        if (result.changed) {
          transaction.update(
            planRef,
            sanitizeForFirestore({ tables: result.tables, lastUpdated: new Date().toISOString() })
          );
        }
      }).catch((error) => {
        console.error(`Error removing guests from floor plan ${roomId}:`, error);
        const planName = rooms.find((r) => r.id === roomId)?.name ?? 'another floor plan';
        showError(`Couldn't remove the guest from "${planName}". Check that plan for a duplicate seat.`);
      });
    }
  };

  /** Seats a guest (removing them from any other seat, on any floor plan). */
  const seatGuest = (guest: RSVP, tableId: string, seatNumber: number) => {
    const guestIds = new Set([guest.id]);
    const next = removeGuestsFromTables(tablesRef.current, guestIds).tables.map((table) =>
      table.id !== tableId
        ? table
        : {
            ...table,
            seating: table.seating.map((seat) =>
              seat.seatNumber === seatNumber ? { ...seat, rsvpId: guest.id, role: guest.rank || null } : seat
            ),
          }
    );
    tablesRef.current = next;
    setTables(next);
    removeGuestsFromOtherPlans(guestIds);
  };

  const handleAssignGuest = (tableId: string, seatNumber: number, guestId: string) => {
    const guest = allGuests.find((g) => g.id === guestId);
    if (!guest) return;
    seatGuest(guest, tableId, seatNumber);
  };

  const normaliseRoomTables = (rawTables: any[]): TableConfig[] => {
    const migratedTables = (rawTables || []).map((table: any, index: number) => {
      const isSquare = table.type === 'square';
      const defaultSeatPlacement = isSquare ? {
        bothSides: false,
        oneSide: false,
        leftEnd: false,
        rightEnd: false,
        noSeats: false,
        topSide: true,
        bottomSide: true,
        leftSide: true,
        rightSide: true,
      } : {
        bothSides: true,
        oneSide: false,
        leftEnd: false,
        rightEnd: false,
        noSeats: false,
        // New explicit rectangular side flags (default both on)
        topSide: table?.seatPlacement?.topSide ?? true,
        bottomSide: table?.seatPlacement?.bottomSide ?? true,
      };

      return {
        ...table,
        seatPlacement: (() => {
          const sp = table.seatPlacement || defaultSeatPlacement;
          // Ensure top/bottom flags exist for rectangular tables
          if (table.type === 'rectangular') {
            const both = sp.bothSides === true;
            const one = sp.oneSide === true;
            const top = sp.topSide ?? (both || one);
            const bottom = sp.bottomSide ?? (both || (!one && both) || (!one && !both ? false : both));
            return { ...sp, topSide: top, bottomSide: bottom };
          }
          return sp;
        })(),
        seatCounts: table.seatCounts || undefined,
        chairColor: table.chairColor || '#e5e7eb',
        seatNumberPriority: table.seatNumberPriority || (index + 1)
      };
    });

    return recalculateSeatNumbers(migratedTables);
  };

  // Helper function to find seats adjacent to or across from a given seat
  const findAdjacentSeats = (table: TableConfig, seatNumber: number): number[] => {
    const seat = table.seating.find(s => s.seatNumber === seatNumber);
    if (!seat) return [];

    const adjacent: number[] = [];
    const threshold = 60; // Distance threshold for "adjacent" (2x chair radius + some margin)

    for (const otherSeat of table.seating) {
      if (otherSeat.seatNumber === seatNumber) continue;

      const dx = otherSeat.x - seat.x;
      const dy = otherSeat.y - seat.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Adjacent: close horizontally or vertically
      if (distance < threshold) {
        adjacent.push(otherSeat.seatNumber);
      }
    }

    return adjacent;
  };

  const findAcrossSeats = (table: TableConfig, seatNumber: number): number[] => {
    const seat = table.seating.find(s => s.seatNumber === seatNumber);
    if (!seat) return [];

    const across: number[] = [];

    // For circular/oval tables, across means opposite side (180 degrees)
    if (table.type === 'circular' || table.type === 'oval') {
      const seatAngle = Math.atan2(seat.y, seat.x);
      const oppositeAngle = seatAngle + Math.PI;

      for (const otherSeat of table.seating) {
        if (otherSeat.seatNumber === seatNumber) continue;
        const otherAngle = Math.atan2(otherSeat.y, otherSeat.x);
        const angleDiff = Math.abs(otherAngle - oppositeAngle);
        const normalizedDiff = Math.min(angleDiff, 2 * Math.PI - angleDiff);
        
        if (normalizedDiff < Math.PI / 6) { // Within 30 degrees of opposite
          across.push(otherSeat.seatNumber);
        }
      }
    } else {
      // For rectangular/square tables, across means opposite side
      const isOnTop = seat.y < 0;
      const isOnBottom = seat.y > 0;
      const isOnLeft = seat.x < 0;
      const isOnRight = seat.x > 0;

      for (const otherSeat of table.seating) {
        if (otherSeat.seatNumber === seatNumber) continue;

        if (isOnTop && otherSeat.y > 0 && Math.abs(otherSeat.x - seat.x) < table.width / 4) {
          across.push(otherSeat.seatNumber);
        } else if (isOnBottom && otherSeat.y < 0 && Math.abs(otherSeat.x - seat.x) < table.width / 4) {
          across.push(otherSeat.seatNumber);
        } else if (isOnLeft && otherSeat.x > 0 && Math.abs(otherSeat.y - seat.y) < table.height / 4) {
          across.push(otherSeat.seatNumber);
        } else if (isOnRight && otherSeat.x < 0 && Math.abs(otherSeat.y - seat.y) < table.height / 4) {
          across.push(otherSeat.seatNumber);
        }
      }
    }

    return across;
  };

  // Group guests by relationships (invitedBy)
  const groupGuestsByRelationships = (guests: RSVP[]): RSVP[][] => {
    const groups: RSVP[][] = [];
    const processed = new Set<string>();
    const inviterMap = new Map<string, RSVP[]>();

    // Build map of inviter -> invitees
    for (const guest of guests) {
      if (guest.invitedBy) {
        if (!inviterMap.has(guest.invitedBy)) {
          inviterMap.set(guest.invitedBy, []);
        }
        inviterMap.get(guest.invitedBy)!.push(guest);
      }
    }

    // Also group guests who invited others (the inviters themselves)
    for (const guest of guests) {
      if (processed.has(guest.id)) continue;

      const group: RSVP[] = [guest];
      processed.add(guest.id);

      // Add guests invited by this guest
      const invitees = inviterMap.get(guest.id) || [];
      for (const invitee of invitees) {
        if (!processed.has(invitee.id)) {
          group.push(invitee);
          processed.add(invitee.id);
        }
      }

      // If this guest was invited by someone, add them to that group
      if (guest.invitedBy) {
        const inviter = guests.find(g => g.id === guest.invitedBy);
        if (inviter && !processed.has(inviter.id)) {
          group.unshift(inviter);
          processed.add(inviter.id);
        }
      }

      if (group.length > 0) {
        groups.push(group);
      }
    }

    // Sort groups by size (largest first)
    return groups.sort((a, b) => b.length - a.length);
  };

  // Auto-populate algorithm
  const autoPopulateGuests = async (reassignAll: boolean) => {
    if (!selectedRoomId) {
      showError('Please select a floor plan first');
      return;
    }

    try {
      // Step 1: Get guests to assign and prepare tables (seats are copied: the loop below mutates them).
      // A guest can only sit on one floor plan, so guests seated on other plans are left where they are.
      const seatedElsewhere = new Set<string>();
      effectiveRoomTablesRef.current.forEach((roomTables, roomId) => {
        if (roomId === tablesRoomIdRef.current) return;
        roomTables.forEach((table) =>
          table.seating?.forEach((seat) => {
            if (seat.rsvpId) seatedElsewhere.add(seat.rsvpId);
          })
        );
      });

      const currentTables = tablesRef.current;
      const workingTables: TableConfig[] = currentTables.map((table) => ({
        ...table,
        seating: table.seating.map((seat) =>
          reassignAll ? { ...seat, rsvpId: null, role: null } : { ...seat }
        ),
      }));
      const seatedHere = new Set<string>();
      for (const table of workingTables) {
        for (const seat of table.seating) {
          if (seat.rsvpId) seatedHere.add(seat.rsvpId);
        }
      }
      const guestsToAssign = allGuests.filter(
        (g) => !seatedElsewhere.has(g.id) && !seatedHere.has(g.id)
      );

      if (guestsToAssign.length === 0) {
        showSuccess('No guests to assign');
        return;
      }

      // Step 2: Group guests by relationships
      const guestGroups = groupGuestsByRelationships(guestsToAssign);

      // Step 3: Collect all available seats
      const availableSeats: Array<{
        tableId: string;
        table: TableConfig;
        seatNumber: number;
        seat: any;
        adjacentSeats: number[];
        acrossSeats: number[];
      }> = [];

      for (const table of workingTables) {
        for (const seat of table.seating) {
          if (!seat.rsvpId) {
            availableSeats.push({
              tableId: table.id,
              table,
              seatNumber: seat.seatNumber,
              seat,
              adjacentSeats: findAdjacentSeats(table, seat.seatNumber),
              acrossSeats: findAcrossSeats(table, seat.seatNumber),
            });
          }
        }
      }

      // Step 4: Sort guests by priority (large groups, small groups, individuals)
      const sortedGuests: RSVP[] = [];

      // Priority: guests sorted by relationship group size
      const remainingGuests = new Set(guestsToAssign.map(g => g.id));
      const remainingGroups = guestGroups.filter(group => 
        group.some(g => remainingGuests.has(g.id))
      );
      for (const group of remainingGroups.sort((a, b) => b.length - a.length)) {
        for (const guest of group) {
          if (remainingGuests.has(guest.id)) {
            sortedGuests.push(guest);
            remainingGuests.delete(guest.id);
          }
        }
      }

      // Step 5: Assign guests
      const assigned = new Set<string>();
      const updatedTables = workingTables;

      for (const guest of sortedGuests) {
        if (assigned.has(guest.id)) continue;

        let bestSeat: { tableId: string; seatNumber: number } | null = null;
        let bestScore = -1;

        // Find best seat for this guest
        for (const availableSeat of availableSeats) {
          // Skip if seat is already assigned in updatedTables
          const table = updatedTables.find(t => t.id === availableSeat.tableId);
          const seat = table?.seating.find(s => s.seatNumber === availableSeat.seatNumber);
          if (seat?.rsvpId) continue;

          let score = 0;

          // Priority: try to sit with related guests
          if (guest.invitedBy) {
            // Check if inviter is already seated nearby
            const inviterTable = updatedTables.find(t => 
              t.seating.some(s => s.rsvpId === guest.invitedBy)
            );
            if (inviterTable?.id === availableSeat.tableId) {
              const inviterSeat = inviterTable.seating.find(s => s.rsvpId === guest.invitedBy);
              if (inviterSeat) {
                if (availableSeat.adjacentSeats.includes(inviterSeat.seatNumber)) {
                  score += 500;
                } else if (availableSeat.acrossSeats.includes(inviterSeat.seatNumber)) {
                  score += 300;
                } else {
                  score += 100; // Same table but not adjacent
                }
              }
            }
          }

          // Check if any invitees are already seated nearby
          const invitees = guestsToAssign.filter(g => g.invitedBy === guest.id);
          for (const invitee of invitees) {
            if (assigned.has(invitee.id)) {
              const inviteeTable = updatedTables.find(t => 
                t.seating.some(s => s.rsvpId === invitee.id)
              );
              if (inviteeTable?.id === availableSeat.tableId) {
                const inviteeSeat = inviteeTable.seating.find(s => s.rsvpId === invitee.id);
                if (inviteeSeat) {
                  if (availableSeat.adjacentSeats.includes(inviteeSeat.seatNumber)) {
                    score += 400;
                  } else if (availableSeat.acrossSeats.includes(inviteeSeat.seatNumber)) {
                    score += 200;
                  }
                }
              }
            }
          }

          if (score > bestScore) {
            bestScore = score;
            bestSeat = { tableId: availableSeat.tableId, seatNumber: availableSeat.seatNumber };
          }
        }

        // Assign guest to best seat
        if (bestSeat) {
          const table = updatedTables.find(t => t.id === bestSeat!.tableId);
          if (table) {
            const seat = table.seating.find(s => s.seatNumber === bestSeat!.seatNumber);
            if (seat) {
              seat.rsvpId = guest.id;
              seat.role = guest.rank || null;
              assigned.add(guest.id);
            }
          }
        }
      }

      tablesRef.current = updatedTables;
      setTables(updatedTables);

      const unseatedCount = guestsToAssign.length - assigned.size;
      if (unseatedCount > 0) {
        showWarning(
          `Seated ${assigned.size} guest(s); ${unseatedCount} could not be seated — add more seats or tables.`
        );
      } else {
        showSuccess(`Auto-populated ${assigned.size} guest(s)`);
      }
    } catch (error) {
      console.error('Error auto-populating guests:', error);
      showError('Failed to auto-populate guests');
    }
  };

  const handleUnassignGuest = (tableId: string, seatNumber: number) => {
    setTables(
      tables.map((table) => {
        if (table.id === tableId) {
          return {
            ...table,
            seating: table.seating.map((seat) =>
              seat.seatNumber === seatNumber
                ? { ...seat, rsvpId: null, role: null }
                : seat
            ),
          };
        }
        return table;
      })
    );
  };

  // Find related guests (those who invited this guest, or who were invited by this guest)
  const findRelatedGuests = (guestId: string): Set<string> => {
    const related = new Set<string>();
    const guest = allGuests.find(g => g.id === guestId);
    if (!guest) return related;

    // Add guests who invited this guest
    if (guest.invitedBy) {
      related.add(guest.invitedBy);
    }

    // Add guests who were invited by this guest
    for (const otherGuest of allGuests) {
      if (otherGuest.invitedBy === guestId) {
        related.add(otherGuest.id);
      }
    }

    return related;
  };

  const handleGuestDragStart = (guest: RSVP, e: React.DragEvent) => {
    setDraggedGuest(guest);
    
    // Find and highlight related guests
    const relatedGuests = findRelatedGuests(guest.id);
    setHighlightedRelatedGuests(relatedGuests);
    
    // Set drag data for HTML5 drag and drop
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', guest.id);
      // Store guest data as JSON for easier retrieval
      e.dataTransfer.setData('application/json', JSON.stringify({ id: guest.id, name: guest.name }));
      
      // Create custom drag image - a circle with guest name (matching seat appearance)
      const dragImage = document.createElement('div');
      dragImage.style.width = '48px';
      dragImage.style.height = '48px';
      dragImage.style.borderRadius = '50%';
      const seatColours = showGuestRelationships
        ? (guestSeatColourMap.get(guest.id) ?? DEFAULT_OCCUPIED_SEAT_COLOURS)
        : NEUTRAL_RELATIONSHIPS_OFF_SEAT_COLOURS;
      dragImage.style.backgroundColor = seatColours.fill;
      dragImage.style.border = `2px solid ${seatColours.stroke}`;
      dragImage.style.display = 'flex';
      dragImage.style.flexDirection = 'column';
      dragImage.style.alignItems = 'center';
      dragImage.style.justifyContent = 'center';
      dragImage.style.opacity = '0.5';
      dragImage.style.position = 'absolute';
      dragImage.style.top = '-1000px'; // Position off-screen
      dragImage.style.left = '-1000px';
      dragImage.style.pointerEvents = 'none';
      dragImage.style.zIndex = '10000';
      dragImage.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.3)';
      
      const combined = [guest.firstName, guest.lastName].filter(Boolean).join(' ').trim();
      const labelSource = combined || guest.name?.trim() || '';
      const nameWords = splitGuestNameIntoSeatLines(labelSource);

      // Create container for name text (one row per word, matching seat circles)
      const nameContainer = document.createElement('div');
      nameContainer.style.display = 'flex';
      nameContainer.style.flexDirection = 'column';
      nameContainer.style.alignItems = 'center';
      nameContainer.style.justifyContent = 'center';
      nameContainer.style.width = '100%';
      nameContainer.style.height = '100%';
      nameContainer.style.padding = '2px';

      for (const word of nameWords) {
        const line = document.createElement('div');
        line.textContent = word;
        line.style.color = '#000000';
        line.style.fontSize = '9px';
        line.style.fontWeight = 'bold';
        line.style.textAlign = 'center';
        line.style.lineHeight = '10px';
        line.style.overflow = 'hidden';
        line.style.textOverflow = 'ellipsis';
        line.style.maxWidth = '44px';
        line.style.whiteSpace = 'nowrap';
        nameContainer.appendChild(line);
      }

      // If no name parts, show initials as fallback
      if (nameWords.length === 0) {
        const initialsText = document.createElement('div');
        initialsText.textContent = guest.name.slice(0, 2).toUpperCase();
        initialsText.style.color = '#000000';
        initialsText.style.fontSize = '12px';
        initialsText.style.fontWeight = 'bold';
        initialsText.style.textAlign = 'center';
        nameContainer.appendChild(initialsText);
      }
      
      dragImage.appendChild(nameContainer);
      document.body.appendChild(dragImage);
      
      // Set the drag image, offset by half the size so cursor is at center
      e.dataTransfer.setDragImage(dragImage, 24, 24);
      
      // Clean up the drag image after a short delay
      setTimeout(() => {
        if (document.body.contains(dragImage)) {
          document.body.removeChild(dragImage);
        }
      }, 0);
    }
  };

  const handleGuestDragEnd = () => {
    // Clear dragged guest after a short delay to allow drop event to process
    // If drop was successful, handleSeatDrop will have already cleared it
    setTimeout(() => {
      setDraggedGuest(null);
      draggedSeatRef.current = null;
      setDraggedSeat(null);
      setIsDraggingSeat(false);
      setHighlightedRelatedGuests(new Set());
    }, 100);
  };

  const handleSeatDrop = (tableId: string, seatNumber: number, overrideGuest?: RSVP | null) => {
    // Use override guest if provided, otherwise use state
    const guestToUse: RSVP | null = overrideGuest !== undefined ? overrideGuest : draggedGuest;
    
    devLog('🎯 [handleSeatDrop] Called', {
      tableId,
      seatNumber,
      hasDraggedSeat: !!draggedSeat,
      hasDraggedGuest: !!draggedGuest,
      hasOverrideGuest: overrideGuest !== undefined,
      guestToUse: guestToUse ? { id: guestToUse.id, name: guestToUse.name } : null,
      draggedSeat,
      stateDraggedGuest: draggedGuest ? { id: draggedGuest.id, name: draggedGuest.name } : null,
    });

    // Handle seat-to-seat drag (swapping). Ref avoids stale state if pointerup runs before React commits setDraggedSeat.
    const activeDraggedSeat = draggedSeatRef.current ?? draggedSeat;
    if (activeDraggedSeat) {
      devLog('🔄 [handleSeatDrop] Handling seat-to-seat drag');
      const sourceTable = tables.find(t => t.id === activeDraggedSeat.tableId);
      const targetTable = tables.find(t => t.id === tableId);
      
      if (!sourceTable || !targetTable) {
        resetSeatDragState();
        return;
      }
      
      const targetSeat = targetTable.seating.find(s => s.seatNumber === seatNumber);
      
      if (!targetSeat) {
        resetSeatDragState();
        return;
      }
      
      // If dropping on the same seat, do nothing but clear drag state
      if (activeDraggedSeat.tableId === tableId && activeDraggedSeat.seatNumber === seatNumber) {
        // Mark that we just dropped on this seat to prevent click event
        justDroppedRef.current = {
          tableId,
          seatNumber,
          timestamp: Date.now(),
        };
        resetSeatDragState();
        return;
      }
      
      // Use drag payload as source-of-truth to avoid stale seat reads during rapid interactions.
      const sourceGuestId = activeDraggedSeat.guestId;
      const targetGuestId = targetSeat.rsvpId;
      const sourceGuest = sourceGuestId ? allGuests.find(g => g.id === sourceGuestId) : null;
      const targetGuest = targetGuestId ? allGuests.find(g => g.id === targetGuestId) : null;
      
      // Swap guests if target seat is occupied, otherwise just move
      const updatedTables = tables.map((table) => {
        if (table.id === activeDraggedSeat.tableId && table.id === tableId) {
          // Same table - swap within table
          return {
            ...table,
            seating: table.seating.map((seat) => {
              if (seat.seatNumber === activeDraggedSeat.seatNumber) {
                return { ...seat, rsvpId: targetGuestId, role: targetGuest?.rank || null };
              }
              if (seat.seatNumber === seatNumber) {
                return { ...seat, rsvpId: sourceGuestId, role: sourceGuest?.rank || null };
              }
              return seat;
            }),
          };
        } else if (table.id === activeDraggedSeat.tableId) {
          // Source table - clear source seat or swap with target guest
          return {
            ...table,
            seating: table.seating.map((seat) =>
              seat.seatNumber === activeDraggedSeat.seatNumber
                ? { ...seat, rsvpId: targetGuestId, role: targetGuest?.rank || null }
                : seat
            ),
          };
        } else if (table.id === tableId) {
          // Target table - assign source guest or swap
          return {
            ...table,
            seating: table.seating.map((seat) =>
              seat.seatNumber === seatNumber
                ? { ...seat, rsvpId: sourceGuestId, role: sourceGuest?.rank || null }
                : seat
            ),
          };
        }
        return table;
      });
      
      setTables(updatedTables);
      
      // Mark that we just dropped on this seat to prevent click event
      justDroppedRef.current = {
        tableId,
        seatNumber,
        timestamp: Date.now(),
      };
      resetSeatDragState();
      
      if (targetGuestId && sourceGuest) {
        showSuccess(`${sourceGuest.name} and ${targetGuest?.name} swapped seats`);
      } else if (sourceGuest) {
        showSuccess(`${sourceGuest.name} moved to seat ${seatNumber}`);
      }
      return;
    }
    
    // Handle sidebar-to-seat drag
    if (!guestToUse) {
      devLog('❌ [handleSeatDrop] No guest to assign, returning');
      resetSeatDragState();
      return;
    }
    
    const activeGuest = guestToUse;
    const currentAssignment = guestSeatAssignments.get(activeGuest.id);
    const isSameSeat =
      currentAssignment?.roomId === tablesRoomIdRef.current &&
      tablesRef.current.some(
        (t) => t.id === tableId && t.seating.some((s) => s.seatNumber === seatNumber && s.rsvpId === activeGuest.id)
      );

    justDroppedRef.current = {
      tableId,
      seatNumber,
      timestamp: Date.now(),
    };
    setDraggedGuest(null);
    setIsOverUnassignZone(false);
    setHighlightedRelatedGuests(new Set());

    if (isSameSeat) {
      return;
    }

    const targetTable = tablesRef.current.find((t) => t.id === tableId);
    const targetGuestId = targetTable?.seating.find((s) => s.seatNumber === seatNumber)?.rsvpId;
    const targetGuest = targetGuestId ? allGuests.find((g) => g.id === targetGuestId) : null;

    // Removes the guest from any other seat on any floor plan, then seats them here.
    seatGuest(activeGuest, tableId, seatNumber);

    if (targetGuest) {
      showSuccess(`${activeGuest.name} replaced ${targetGuest.name} (now unassigned)`);
    } else {
      showSuccess(`${activeGuest.name} assigned to seat ${seatNumber}`);
    }
  };

  const handleSeatDragStart = (tableId: string, seatNumber: number, guestId: string) => {
    const payload = { tableId, seatNumber, guestId };
    draggedSeatRef.current = payload;
    setDraggedSeat(payload);
    setIsDraggingSeat(true);
    
    // Find and highlight related guests
    const relatedGuests = findRelatedGuests(guestId);
    setHighlightedRelatedGuests(relatedGuests);
    
    // Get guest name for drag preview
    const guest = allGuests.find(g => g.id === guestId);
    if (guest) {
      const seatColours = showGuestRelationships
        ? (guestSeatColourMap.get(guestId) ?? DEFAULT_OCCUPIED_SEAT_COLOURS)
        : NEUTRAL_RELATIONSHIPS_OFF_SEAT_COLOURS;
      const st = stageRef.current;
      const pointerPos = st?.getPointerPosition();
      if (st && pointerPos) {
        const rect = st.container().getBoundingClientRect();
        queueDragPreviewUpdate({
          x: rect.left + pointerPos.x,
          y: rect.top + pointerPos.y,
          guestName: guest.name,
          fill: seatColours.fill,
          stroke: seatColours.stroke,
        });
      }

      clearSeatDragPointerListeners();

      // Create drag preview that will follow pointer
      const handlePointerMove = (e: PointerEvent) => {
        queueDragPreviewUpdate({
          x: e.clientX,
          y: e.clientY,
          guestName: guest.name,
          fill: seatColours.fill,
          stroke: seatColours.stroke,
        });
        // Check if over unassign zone (below header)
        updateUnassignZone(e.clientY >= 64 && e.clientY < 144);
      };
      
      const handlePointerUp = (e: PointerEvent) => {
        clearSeatDragPointerListeners();

        // Check if dropped in unassign zone (below header)
        if (e.clientY >= 64 && e.clientY < 144) {
          // Unassign guest from seat
          const sourceTable = tables.find(t => t.id === tableId);
          if (sourceTable) {
            const updatedTables = tables.map((table) => {
              if (table.id === tableId) {
                return {
                  ...table,
                  seating: table.seating.map((seat) =>
                    seat.seatNumber === seatNumber
                      ? { ...seat, rsvpId: null, role: null }
                      : seat
                  ),
                };
              }
              return table;
            });
            setTables(updatedTables);
            showSuccess(`${guest.name} unassigned from seat`);
          }
          resetSeatDragState();
        }
        // Otherwise, let the stage's onMouseUp handler handle the drop
        // It will call handleSeatDrop which will clear the state
      };

      seatDragPointerListenersRef.current = {
        move: handlePointerMove,
        up: handlePointerUp,
      };
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
      window.addEventListener('pointercancel', handlePointerUp);
    }
  };

  // Handle HTML5 drop events on the stage container
  const handleStageDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const seatFromPointer = draggedSeatRef.current ?? draggedSeat;
    
    devLog('🎯 [handleStageDrop] Called', {
      clientX: e.clientX,
      clientY: e.clientY,
      hasDraggedSeat: !!seatFromPointer,
      hasDraggedGuest: !!draggedGuest,
    });
    
    // Always try to get guest data from the drag event as a fallback
    // This ensures we can handle the drop even if state was cleared
    const guestId = e.dataTransfer?.getData('text/plain');
    let guestFromEvent: RSVP | null = null;
    if (guestId) {
      guestFromEvent = allGuests.find(g => g.id === guestId) || null;
    }
    
    // Use draggedGuest from state, or fall back to guest from event
    const activeDraggedGuest = draggedGuest || guestFromEvent;
    
    devLog('👤 [handleStageDrop] Active dragged guest', {
      fromState: !!draggedGuest,
      fromEvent: !!guestFromEvent,
      guest: activeDraggedGuest ? { id: activeDraggedGuest.id, name: activeDraggedGuest.name } : null,
    });

    // Check if dropped in unassign zone (below header, top 80px area)
    // Header is typically around 64px, so zone starts at ~80px
    if (e.clientY >= 64 && e.clientY < 144) {
      if (seatFromPointer) {
        // Unassign guest from seat
        const sourceTable = tables.find(t => t.id === seatFromPointer.tableId);
        if (sourceTable) {
          const updatedTables = tables.map((table) => {
            if (table.id === seatFromPointer.tableId) {
              return {
                ...table,
                seating: table.seating.map((seat) =>
                  seat.seatNumber === seatFromPointer.seatNumber
                    ? { ...seat, rsvpId: null, role: null }
                    : seat
                ),
              };
            }
            return table;
          });
          setTables(updatedTables);
          
          const guest = allGuests.find(g => g.id === seatFromPointer.guestId);
          if (guest) {
            showSuccess(`${guest.name} unassigned from seat`);
          }
        }
        resetSeatDragState();
        return;
      } else if (activeDraggedGuest) {
        // Unassign guest (remove from any seat they're in)
        const guestIds = new Set([activeDraggedGuest.id]);
        setTables(removeGuestsFromTables(tables, guestIds).tables);
        removeGuestsFromOtherPlans(guestIds);
        setDraggedGuest(null);
        setIsOverUnassignZone(false);
        setHighlightedRelatedGuests(new Set());
        showSuccess(`${activeDraggedGuest.name} unassigned from seat`);
        return;
      }
    }

    // Handle seat-to-seat drag
    if (seatFromPointer && stageRef.current) {
      const stage = stageRef.current;
      const container = stage.container();
      const rect = container.getBoundingClientRect();
      
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      const stageX = (x - stagePos.x) / scale;
      const stageY = (y - stagePos.y) / scale;
      
      const seatRadius = 24;
      let foundSeat: { tableId: string; seatNumber: number } | null = null;
      
      for (const table of tables) {
        const rotRad = (table.rotation * Math.PI) / 180;
        const cos = Math.cos(-rotRad);
        const sin = Math.sin(-rotRad);
        
        const dx = stageX - table.x;
        const dy = stageY - table.y;
        const localX = dx * cos - dy * sin;
        const localY = dx * sin + dy * cos;
        
        for (const seat of table.seating) {
          const seatX = seat.x;
          const seatY = seat.y;
          const distance = Math.sqrt(
            Math.pow(localX - seatX, 2) + Math.pow(localY - seatY, 2)
          );
          
          if (distance <= seatRadius) {
            foundSeat = { tableId: table.id, seatNumber: seat.seatNumber };
            break;
          }
        }
        
        if (foundSeat) break;
      }
      
      if (foundSeat) {
        handleSeatDrop(foundSeat.tableId, foundSeat.seatNumber);
      } else {
        resetSeatDragState();
      }
      return;
    }

    // Handle sidebar-to-seat drag
    // Use the active dragged guest (from state or event)
    if (!activeDraggedGuest || !stageRef.current) {
      setDraggedGuest(null);
      setHighlightedRelatedGuests(new Set());
      return;
    }
    
    // Ensure state is set for consistency
    if (!draggedGuest && activeDraggedGuest) {
      setDraggedGuest(activeDraggedGuest);
    }

    const stage = stageRef.current;
    const container = stage.container();
    const rect = container.getBoundingClientRect();
    
    // Get drop position relative to container
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Convert to stage coordinates
    const stageX = (x - stagePos.x) / scale;
    const stageY = (y - stagePos.y) / scale;
    
    // Find which seat is at this position
    const seatRadius = 24; // Chair radius
    let foundSeat: { tableId: string; seatNumber: number } | null = null;
    
    for (const table of tables) {
      // Account for table rotation
      const rotRad = (table.rotation * Math.PI) / 180;
      const cos = Math.cos(-rotRad);
      const sin = Math.sin(-rotRad);
      
      // Transform point to table's local coordinate system
      const dx = stageX - table.x;
      const dy = stageY - table.y;
      const localX = dx * cos - dy * sin;
      const localY = dx * sin + dy * cos;
      
      // Check each seat
      for (const seat of table.seating) {
        // Seat position in table's local coordinates
        const seatX = seat.x;
        const seatY = seat.y;
        const distance = Math.sqrt(
          Math.pow(localX - seatX, 2) + Math.pow(localY - seatY, 2)
        );
        
        if (distance <= seatRadius) {
          foundSeat = { tableId: table.id, seatNumber: seat.seatNumber };
          break;
        }
      }
      
      if (foundSeat) break;
    }
    
    if (foundSeat) {
      devLog('✅ [handleStageDrop] Found seat, delegating to handleSeatDrop', {
        tableId: foundSeat.tableId,
        seatNumber: foundSeat.seatNumber,
        activeDraggedGuest: activeDraggedGuest ? { id: activeDraggedGuest.id, name: activeDraggedGuest.name } : null,
      });
      
      // Ensure draggedGuest is set for consistency
      if (!draggedGuest && activeDraggedGuest) {
        setDraggedGuest(activeDraggedGuest);
      }
      
      // Delegate to handleSeatDrop which has the comprehensive unassignment logic
      // Pass the guest directly to avoid state timing issues
      handleSeatDrop(foundSeat.tableId, foundSeat.seatNumber, activeDraggedGuest);
    } else {
      devLog('❌ [handleStageDrop] No seat found at drop location');
      setDraggedGuest(null);
      setHighlightedRelatedGuests(new Set());
    }
  };

  const handleStageDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if over unassign zone (below header)
    updateUnassignZone(e.clientY >= 64 && e.clientY < 144);
    
    if (e.dataTransfer) {
      // Always allow drop if we're dragging a guest or seat
      if (isDraggingSeat || draggedGuest) {
        e.dataTransfer.dropEffect = 'move';
      } else {
        e.dataTransfer.dropEffect = 'copy';
      }
    }
  };

  const guestNameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of allGuests) {
      if (!g.id) continue;
      const combined = [g.firstName, g.lastName].filter(Boolean).join(' ').trim();
      const display = combined || g.name?.trim() || '';
      if (display) m.set(g.id, display);
    }
    return m;
  }, [allGuests]);

  const getGuestName = useCallback((rsvpId: string | null): string | null => {
    if (!rsvpId) return null;
    const name = guestNameMap.get(rsvpId);
    if (name) return name;
    // Seat held by someone no longer on the guest list (see orphanedSeatGuestIds).
    return guestsLoadedFromServer ? 'Cancelled' : null;
  }, [guestNameMap, guestsLoadedFromServer]);

  const getGuestSeatColours = useCallback(
    (rsvpId: string) =>
      showGuestRelationships
        ? (guestSeatColourMap.get(rsvpId) ?? null)
        : NEUTRAL_RELATIONSHIPS_OFF_SEAT_COLOURS,
    [guestSeatColourMap, showGuestRelationships]
  );

  const getGuestPrefixBorderColour = useCallback(
    (rsvpId: string) => prefixBorderByRsvpId.get(rsvpId) ?? null,
    [prefixBorderByRsvpId]
  );

  const effectiveHighlightedRelatedGuests = useMemo(
    () =>
      showGuestRelationships
        ? highlightedRelatedGuests
        : new Set<string>(),
    [showGuestRelationships, highlightedRelatedGuests]
  );

  useEffect(() => {
    if (!showGuestRelationships && highlightedRelatedGuests.size > 0) {
      setHighlightedRelatedGuests(new Set());
    }
  }, [showGuestRelationships, highlightedRelatedGuests]);

  const { availableGuests, assignedGuests } = useMemo(() => {
    const assignedGuestIds = new Set<string>();

    effectiveRoomTables.forEach((roomTables) => {
      roomTables.forEach((table: any) => {
        if (table.seating) {
          table.seating.forEach((seat: any) => {
            if (seat.rsvpId) {
              assignedGuestIds.add(seat.rsvpId);
            }
          });
        }
      });
    });

    const nextAvailableGuests: RSVP[] = [];
    const nextAssignedGuests: RSVP[] = [];
    allGuests.forEach((guest) => {
      if (assignedGuestIds.has(guest.id)) {
        nextAssignedGuests.push(guest);
      } else {
        nextAvailableGuests.push(guest);
      }
    });

    return {
      availableGuests: nextAvailableGuests,
      assignedGuests: nextAssignedGuests,
    };
  }, [effectiveRoomTables, allGuests]);

  /**
   * Seats still held by guests who have cancelled, sent apologies or been removed. They look empty
   * on the plan but block the seat, so they're listed for clearing. Only computed once the guest
   * list has come from the server, so a partial offline cache never flags real guests.
   */
  const orphanedSeatGuestIds = useMemo(() => {
    if (!guestsLoadedFromServer || isMultiTenant === null) return [];
    const known = new Set(allGuests.map((g) => g.id));
    const orphaned = new Set<string>();
    effectiveRoomTables.forEach((roomTables) =>
      roomTables.forEach((table) =>
        table.seating?.forEach((seat) => {
          if (seat.rsvpId && !known.has(seat.rsvpId)) orphaned.add(seat.rsvpId);
        })
      )
    );
    return [...orphaned];
  }, [guestsLoadedFromServer, isMultiTenant, allGuests, effectiveRoomTables]);

  const clearOrphanedSeats = async () => {
    const count = orphanedSeatGuestIds.length;
    const result = await confirmAction({
      title: 'Clear cancelled guests?',
      text: `Free the ${count} seat${count > 1 ? 's' : ''} held by guests who are no longer attending, on every floor plan?`,
      confirmButtonText: 'Clear seats',
      icon: 'question',
    });
    if (!result.isConfirmed) return;
    const ids = new Set(orphanedSeatGuestIds);
    const cleared = removeGuestsFromTables(tablesRef.current, ids);
    if (cleared.changed) {
      tablesRef.current = cleared.tables;
      setTables(cleared.tables);
    }
    removeGuestsFromOtherPlans(ids);
    showSuccess(`Cleared ${count} seat${count > 1 ? 's' : ''}`);
  };

  const handleZoomIn = () => {
    const newScale = Math.min(scale * 1.2, 3);
    setScale(newScale);
    scheduleViewportPersist();
  };

  const handleZoomOut = () => {
    const newScale = Math.max(scale / 1.2, 0.3);
    setScale(newScale);
    scheduleViewportPersist();
  };

  const handleResetView = () => {
    setScale(1);
    setStagePos({ x: 0, y: 0 });
    scheduleViewportPersist();
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    // React/browsers can dispatch wheel via passive listeners. Only prevent default when cancelable.
    if (e.cancelable) {
      e.preventDefault();
    }
    e.stopPropagation();

    const stage = stageRef.current;
    if (!stage) {
      return;
    }

    const oldScale = scale;
    const pointer = stage.getPointerPosition();
    if (!pointer) {
      return;
    }

    const mousePointTo = {
      x: (pointer.x - stagePos.x) / oldScale,
      y: (pointer.y - stagePos.y) / oldScale,
    };

    const newScale = e.deltaY < 0 ? oldScale * 1.1 : oldScale / 1.1;
    const clampedScale = Math.max(0.3, Math.min(3, newScale));

    setScale(clampedScale);
    setStagePos({
      x: pointer.x - mousePointTo.x * clampedScale,
      y: pointer.y - mousePointTo.y * clampedScale,
    });
    scheduleViewportPersist();
  };

  const handleSaveAsTemplate = async () => {
    if (!unitId) return;

    const result = await Swal.fire({
      title: 'Save as Template',
      text: 'Enter a name for this table layout template:',
      input: 'text',
      inputPlaceholder: 'e.g., Standard 50-Person Layout',
      showCancelButton: true,
      confirmButtonText: 'Save Template',
      confirmButtonColor: '#1E3A5F',
      cancelButtonColor: '#6c757d',
    });

    if (result.isConfirmed && result.value) {
      try {
        const templateData = {
          name: result.value,
          tables: tables.map((t) => {
            // Save all table configuration properties
            const tableData: any = {
            type: t.type,
            seats: t.seats,
            name: t.name,
              x: t.x,
              y: t.y,
            width: t.width,
            height: t.height,
              rotation: t.rotation || 0,
              color: t.color || '#ffffff',
              chairColor: t.chairColor || '#e5e7eb',
              seatNumberPriority: t.seatNumberPriority ?? 999,
            };

            // Save seat placement configuration
            if (t.seatPlacement) {
              tableData.seatPlacement = {
                bothSides: t.seatPlacement.bothSides ?? false,
                oneSide: t.seatPlacement.oneSide ?? false,
                leftEnd: t.seatPlacement.leftEnd ?? false,
                rightEnd: t.seatPlacement.rightEnd ?? false,
                noSeats: t.seatPlacement.noSeats ?? false,
                // Square table specific options
                topSide: t.seatPlacement.topSide,
                bottomSide: t.seatPlacement.bottomSide,
                leftSide: t.seatPlacement.leftSide,
                rightSide: t.seatPlacement.rightSide,
              };
            }

            // Save per-side seat counts (important for custom seat distributions)
            if (t.seatCounts) {
              tableData.seatCounts = {
                top: t.seatCounts.top,
                bottom: t.seatCounts.bottom,
                left: t.seatCounts.left,
                right: t.seatCounts.right,
                leftEnd: t.seatCounts.leftEnd,
                rightEnd: t.seatCounts.rightEnd,
              };
            }

            // Persist seat metadata with template
            if (Array.isArray(t.seating)) {
              tableData.seating = t.seating.map((seat: any, index: number) => ({
                index,
                seatNumber: seat?.seatNumber ?? index + 1,
              }));
            }

            return tableData;
          }),
          createdAt: new Date().toISOString(),
        };
        
        // Sanitize data to remove undefined values
        const sanitizedTemplate = sanitizeForFirestore(templateData);
        
        await addDoc(collection(db, 'units', unitId, 'tableTemplates'), sanitizedTemplate);

        showSuccess(`Template "${result.value}" saved!`);
      } catch (error) {
        console.error('Error saving template:', error);
        showError('Failed to save template');
      }
    }
  };

  const loadTablePlanVersions = useCallback(async () => {
    if (!unitId || !eventId || !selectedRoomId) {
      setTablePlanVersions([]);
      setVersionsAccessDenied(false);
      return;
    }

    setVersionsLoading(true);
    setVersionsAccessDenied(false);
    try {
      const snapshot = await getDocs(
        query(versionsCollection(selectedRoomId), orderBy('createdAt', 'desc'), limit(VERSIONS_LIST_LIMIT))
      );

      const versions = snapshot.docs
        .map((docSnap) => {
          const data = docSnap.data();
          const createdAtRaw = data.createdAt;
          const createdAt =
            typeof createdAtRaw === 'string'
              ? createdAtRaw
              : createdAtRaw?.toDate?.()?.toISOString?.() ?? new Date().toISOString();
          const lastUpdatedRaw = data.lastUpdated;
          const lastUpdated =
            typeof lastUpdatedRaw === 'string'
              ? lastUpdatedRaw
              : lastUpdatedRaw?.toDate?.()?.toISOString?.() ?? createdAt;
          const restoredTables = normaliseRoomTables(data.tables || []);
          return {
            id: docSnap.id,
            createdAt,
            lastUpdated,
            capturedAt: createdAt,
            tables: restoredTables,
            scale: data.viewport?.scale || 1,
            stagePos: data.viewport?.position || { x: 0, y: 0 },
            reason: typeof data.reason === 'string' ? data.reason : undefined,
            createdByUid: typeof data.createdByUid === 'string' ? data.createdByUid : undefined,
          } as TablePlanVersionEntry;
        });

      setTablePlanVersions(versions);
    } catch (error) {
      const err = error as any;
      if (err?.code === 'permission-denied') {
        adminWarn('Versions unavailable: no permission for versions subcollection');
        setVersionsAccessDenied(true);
        setTablePlanVersions([]);
      } else {
        console.error('Error loading table plan versions:', error);
        showError('Failed to load versions');
      }
    } finally {
      setVersionsLoading(false);
    }
  }, [eventId, selectedRoomId, unitId, versionsCollection]);

  useEffect(() => {
    if (showVersionsModal) {
      void loadTablePlanVersions();
    }
  }, [showVersionsModal, loadTablePlanVersions]);

  const handleUndo = useCallback(async () => {
    const previous = undoHistoryRef.current.pop();
    if (!previous) {
      showError('Nothing to undo');
      return;
    }

    // Applied without recording a new undo step, then saved straight away.
    ignoreHistorySignatureRef.current = getTablesSignature(previous);
    tablesRef.current = previous;
    setTables(previous);
    await autoSaveTablePlanRef.current('undo');
  }, [getTablesSignature]);
  const handleUndoRef = useRef(handleUndo);
  handleUndoRef.current = handleUndo;

  const handleRestoreVersion = useCallback(
    async (version: TablePlanVersionEntry) => {
      const result = await confirmAction({
        title: 'Restore this version?',
        text: `This will replace the current layout with the version from ${new Date(version.createdAt).toLocaleString()}. You can use Undo afterwards.`,
        confirmButtonText: 'Restore',
        icon: 'warning',
      });

      if (!result.isConfirmed) {
        return;
      }

      // Guests who have since been seated on another floor plan stay there (one seat per guest).
      const seatedElsewhere = new Set<string>();
      effectiveRoomTablesRef.current.forEach((roomTables, roomId) => {
        if (roomId === tablesRoomIdRef.current) return;
        roomTables.forEach((t) => t.seating?.forEach((s) => s.rsvpId && seatedElsewhere.add(s.rsvpId)));
      });
      const restored = removeGuestsFromTables(version.tables, seatedElsewhere);
      const skipped = restored.changed
        ? version.tables.flatMap((t) => t.seating).filter((s) => s.rsvpId && seatedElsewhere.has(s.rsvpId)).length
        : 0;

      // Recorded as an undo step, so a restore can itself be undone.
      tablesRef.current = restored.tables;
      setTables(restored.tables);
      await autoSaveTablePlanRef.current('restore');
      showSuccess(
        `Restored version from ${new Date(version.createdAt).toLocaleString()}` +
          (skipped > 0 ? ` (${skipped} guest(s) now seated on another plan were left there)` : '')
      );
      setShowVersionsModal(false);
    },
    []
  );

  const handleLoadTemplate = async () => {
    if (!unitId) return;

    try {
      const templatesSnapshot = await getDocs(
        collection(db, 'units', unitId, 'tableTemplates')
      );

      if (templatesSnapshot.empty) {
        showError('No templates found');
        return;
      }

      const templateOptions: Record<string, string> = {};
      templatesSnapshot.forEach((docSnap) => {
        templateOptions[docSnap.id] = docSnap.data().name;
      });

      const result = await Swal.fire({
        title: 'Load Template',
        text: 'Select a template to load:',
        input: 'select',
        inputOptions: templateOptions,
        showCancelButton: true,
        confirmButtonText: 'Load Template',
        confirmButtonColor: '#1E3A5F',
        cancelButtonColor: '#6c757d',
      });

      if (result.isConfirmed && result.value) {
        const templateDoc = await getDoc(
          doc(db, 'units', unitId, 'tableTemplates', result.value)
        );

        if (templateDoc.exists()) {
          const templateData = templateDoc.data();
          const newTables: TableConfig[] = templateData.tables.map((t: any, index: number) => {
            // Use saved position if available, otherwise use grid layout
            const savedX = t.x !== undefined ? t.x : 200 + (index % 3) * 300;
            const savedY = t.y !== undefined ? t.y : 200 + Math.floor(index / 3) * 300;
            
            const baseTable: TableConfig = {
              id: `table-${Date.now()}-${index}`,
              type: t.type,
              seats: t.seats,
              name: t.name || `Table ${index + 1}`,
              x: savedX,
              y: savedY,
              rotation: t.rotation ?? 0,
              width: t.width ?? 150,
              height: t.height ?? 150,
              color: t.color ?? '#ffffff',
              chairColor: t.chairColor ?? '#e5e7eb',
              seatNumberPriority: t.seatNumberPriority ?? (index + 1),
              // Restore seat placement configuration
              seatPlacement: t.seatPlacement || {
                bothSides: true,
                oneSide: false,
                leftEnd: false,
                rightEnd: false,
                noSeats: false,
                // Square table defaults
                topSide: true,
                bottomSide: true,
                leftSide: true,
                rightSide: true,
              },
              // Restore per-side seat counts if available
              seatCounts: t.seatCounts ? {
                top: t.seatCounts.top,
                bottom: t.seatCounts.bottom,
                left: t.seatCounts.left,
                right: t.seatCounts.right,
                leftEnd: t.seatCounts.leftEnd,
                rightEnd: t.seatCounts.rightEnd,
              } : undefined,
              // Initialize seating array (will be recalculated)
              seating: Array.from({ length: t.seats }, (_, i) => ({
                seatNumber: i + 1,
                rsvpId: null,
                role: null,
                x: 0,
                y: 0,
                tag: null,
              })),
            };
            // Recalculate seating positions based on the restored configuration
            const recalculatedTable = calculateSeatingPositions(baseTable);

            return recalculatedTable;
          });

          const recalculatedTables = recalculateSeatNumbers(newTables);
          setTables(recalculatedTables);
          showSuccess('Template loaded successfully!');
        }
      }
    } catch (error) {
      console.error('Error loading template:', error);
      showError('Failed to load template');
    }
  };

  /** Export floor plan as a high-resolution PNG, viewport-independent. */
  const handleExportImage = () => {
    const stage = stageRef.current;
    if (!stage) {
      showError('Cannot export: canvas not ready');
      return;
    }
    if (tables.length === 0) {
      showError('No tables to export');
      return;
    }

    try {
      // Tight bounding box of every table body and seat centre (table x/y is the centre; rotation
      // included), plus a margin covering what extends beyond a seat centre:
      //   - seat circle radius       ≈ 28 plan units
      //   - outside-offset to label  ≈ 46 plan units
      //   - guest name text extent   ≈ 80 plan units  (wide names at small font)
      //   - breathing room           ≈ 20 plan units
      // Total extra beyond seat centre ≈ 174 plan units → use 180 to be safe.
      const LABEL_MARGIN = 180;
      const bounds = getTablesBounds(tables);
      if (!bounds) {
        showError('No tables to export');
        return;
      }
      const { minX: bMinX, minY: bMinY, maxX: bMaxX, maxY: bMaxY } = bounds;

      const dataUrl = stage.toDataURL({
        pixelRatio: 3,
        mimeType: 'image/png',
        quality: 1.0,
        omitGrid: true,
        omitBackground: true,
        frame: {
          x: bMinX - LABEL_MARGIN,
          y: bMinY - LABEL_MARGIN,
          width: bMaxX - bMinX + LABEL_MARGIN * 2,
          height: bMaxY - bMinY + LABEL_MARGIN * 2,
        },
      });

      const link = document.createElement('a');
      link.download = `table-plan-${eventId}.png`;
      link.href = dataUrl;
      link.click();
      showSuccess('Table plan exported as image');
    } catch (error) {
      console.error('Error exporting table plan image:', error);
      showError('Failed to export table plan image');
    }
  };

  const handleExportPDFCustom = () => {
    setShowPDFExportModal(true);
  };

  const selectedTable =
    selectedTableIds.size === 1 ? tables.find((t) => selectedTableIds.has(t.id)) ?? null : null;

  /** Plain property edits (name, colour, rotation) that don't change the seat layout. */
  const updateSelectedTables = (patch: Partial<TableConfig>) => {
    setTables((prev) => prev.map((t) => (selectedTableIds.has(t.id) ? { ...t, ...patch } : t)));
  };

  return (
    <div className="flex h-screen max-h-screen flex-col overflow-hidden bg-background text-foreground">
      <Header />
      
      {/* Unassign Zone - appears when dragging a guest */}
      {(isDraggingSeat || draggedGuest) && (
        <div
          className={`fixed top-16 left-0 right-0 h-20 z-40 transition-all duration-200 ${
            isOverUnassignZone
              ? 'bg-red-100 border-b-4 border-red-500 dark:bg-red-950/60 dark:border-red-500'
              : 'bg-muted border-b-2 border-border'
          }`}
          style={{
            pointerEvents: 'none',
          }}
        >
          <div className="flex items-center justify-center h-full">
            <div className={`text-center px-4 py-2 rounded-lg ${
              isOverUnassignZone
                ? 'bg-red-500 text-white'
                : 'bg-card text-muted-foreground border-2 border-dashed border-border'
            }`}>
              <div className="text-sm font-semibold">
                {isOverUnassignZone ? 'Drop here to unassign' : 'Drop here to unassign guest'}
              </div>
            </div>
          </div>
        </div>
      )}
      
      <main className="flex min-h-0 flex-1 flex-col bg-background text-foreground">
        {/* Toolbar */}
        <div className="flex-shrink-0 border-b border-border bg-background py-3">
          <DashboardContentContainer>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <Button
                variant="ghost"
                onClick={() => navigate(unitId ? `/unit/${unitId}/event/${eventId}` : `/event/${eventId}`)}
              >
                ← Back
              </Button>
              <div>
                <h1 className="text-xl font-bold">Visual Table Planner</h1>
                <p className="text-sm text-muted-foreground">
                  {loading
                    ? 'Loading guest list…'
                    : `${assignedGuests.length} of ${allGuests.length} guests seated`}
                </p>
              </div>
              
              {/* Auto-save indicator */}
              {selectedRoomId && (
                <div className="text-xs text-muted-foreground flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    {liveSyncStatus === 'live' ? (
                      <>
                        <div className="h-2 w-2 rounded-full bg-emerald-500" />
                        Live sync on
                      </>
                    ) : liveSyncStatus === 'degraded' ? (
                      <>
                        <div className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                        Live sync reconnecting
                      </>
                    ) : (
                      <>
                        <div className="h-2 w-2 rounded-full bg-slate-400 animate-pulse" />
                        Live sync connecting
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {autoSaving ? (
                      <>
                        <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
                        Saving...
                      </>
                    ) : lastSaved ? (
                      <>
                        <div className="h-2 w-2 rounded-full bg-green-500" />
                        Saved {lastSaved.toLocaleTimeString()}
                      </>
                    ) : null}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowRankBorders((prev) => !prev)}
              >
                Rank Borders: {showRankBorders ? 'On' : 'Off'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowGuestRelationships((prev) => !prev)}
              >
                Guest Relationships: {showGuestRelationships ? 'On' : 'Off'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => void handleUndo()} title="Undo (Ctrl+Z)">
                <Undo2 className="mr-2 h-4 w-4" />
                Undo
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowVersionsModal(true)}>
                <History className="mr-2 h-4 w-4" />
                Versions
              </Button>
              <Button variant="outline" size="sm" onClick={handleLoadTemplate}>
                <FolderOpen className="mr-2 h-4 w-4" />
                Load Template
              </Button>
              <Button variant="outline" size="sm" onClick={handleSaveAsTemplate}>
                <Upload className="mr-2 h-4 w-4" />
                Save Template
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowAutoPopulateModal(true)}>
                <Users className="mr-2 h-4 w-4" />
                Auto Populate
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportImage}>
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
              <Button variant="default" size="sm" onClick={handleExportPDFCustom}>
                <Download className="mr-2 h-4 w-4" />
                Designer
              </Button>
            </div>
          </div>
          </DashboardContentContainer>
        </div>

        {!loading && (
          <div className="flex-shrink-0 border-b border-border bg-muted/40 py-2">
            <DashboardContentContainer>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground shrink-0">
                Table plans
              </span>
              <div className="flex flex-wrap items-center gap-2 min-w-0 flex-1">
                {rooms.map((room) => {
                  const isActive = room.id === selectedRoomId;
                  return (
                    <div
                      key={room.id}
                      className={`flex items-center gap-0.5 shrink-0 rounded-md border bg-background pl-2 pr-0.5 py-0.5 shadow-sm ${
                        isActive ? 'ring-2 ring-primary border-primary' : 'border-border'
                      }`}
                    >
                      <button
                        type="button"
                        className={`max-w-[200px] truncate px-1.5 py-1 text-sm font-medium rounded hover:bg-muted/80 ${
                          isActive ? 'text-primary' : 'text-foreground'
                        }`}
                        onClick={() => void handleRoomChange(room.id)}
                        title={room.name}
                      >
                        {room.name}
                      </button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        onClick={() => setRenameTarget({ id: room.id, name: room.name })}
                        title="Rename design plan"
                        aria-label={`Rename ${room.name}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0 text-destructive hover:text-destructive"
                        onClick={() => void deleteDesign(room.id)}
                        title="Remove floor plan"
                        aria-label={`Remove ${room.name}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  );
                })}
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  className="h-8 w-8 shrink-0"
                  onClick={() => setShowCreateRoomModal(true)}
                  title="Add design plan"
                  aria-label="Add design plan"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
            </DashboardContentContainer>
          </div>
        )}

        <div
          className={`flex-shrink-0 border-b border-border bg-background py-1.5 ${
            orphanedSeatGuestIds.length > 0 ? '' : 'md:hidden'
          }`}
        >
          <DashboardContentContainer>
          <div className="flex flex-wrap items-center gap-2">
            {orphanedSeatGuestIds.length > 0 && (
              <div className="flex items-center gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 px-2 py-1 text-xs text-foreground">
                <span>
                  {orphanedSeatGuestIds.length} seat{orphanedSeatGuestIds.length > 1 ? 's are' : ' is'} held
                  by guests no longer attending (shown as “Cancelled”).
                </span>
                <Button variant="outline" size="sm" className="h-6 px-2 text-xs" onClick={() => void clearOrphanedSeats()}>
                  Clear
                </Button>
              </div>
            )}
            <span className="text-xs text-muted-foreground md:hidden">
              Tap a seat to assign a guest.
            </span>
          </div>
          </DashboardContentContainer>
        </div>

        {/* Main Content: paint toolbar + title immediately for LCP; defer heavy canvas until data is ready */}
        {loading ? (
        <div className="flex min-h-0 flex-1 items-center justify-center bg-muted">
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <p className="text-muted-foreground">Loading table planner…</p>
          </div>
        </div>
        ) : (
        <div className="flex min-h-0 flex-1 overflow-hidden">
          {/* Canvas Area */}
          <div className="relative min-h-0 flex-1 bg-muted">
            {/* Canvas Controls */}
            <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
              <Button size="sm" onClick={() => handleAddTable('rectangular')}>
                <Plus className="mr-2 h-4 w-4" />
                Add Table
              </Button>
              <div className="flex flex-col gap-1 rounded-lg border border-border bg-card p-2 text-card-foreground shadow-md">
                <Button variant="outline" size="sm" onClick={handleZoomIn}>
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={handleZoomOut}>
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={handleResetView}>
                  <RotateCcw className="h-4 w-4" />
                </Button>
                <div className="py-1 text-center text-xs font-semibold text-foreground">
                  {Math.round(scale * 100)}%
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowGrid(!showGrid)}
              >
                Grid: {showGrid ? 'On' : 'Off'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSnapToGrid(!snapToGrid)}
              >
                Grid (0.1 m): {snapToGrid ? 'On' : 'Off'}
              </Button>
              
              {/* Duplicate button - only visible when at least one table is selected */}
              {selectedTableIds.size > 0 && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDuplicateTable}
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    Duplicate
                  </Button>
                  
                  {/* Table Configuration Panel - only show when exactly one table is selected */}
                  {selectedTableIds.size === 1 ? (
                  <div className="max-w-xs space-y-3 rounded-lg border border-border bg-card p-3 text-card-foreground shadow-md">
                    <h3 className="border-b border-border pb-2 text-sm font-semibold text-foreground">Table Settings</h3>
                    
                    {/* Table Name */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-foreground">Name</label>
                      <Input
                        value={selectedTable?.name ?? ''}
                        onChange={(e) => updateSelectedTables({ name: e.target.value })}
                        className="h-8 text-sm"
                      />
                    </div>

                    {/* Table Type */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-foreground">Type</label>
                      <Select
                        value={selectedTable?.type || 'rectangular'}
                        onValueChange={(value) =>
                          // Per-side counts belong to the old shape; guests keep their seat order.
                          applyTableLayoutChange(selectedTableIds, (t) =>
                            reflowTable(t, { type: value as TableConfig['type'], seatCounts: undefined })
                          )
                        }
                      >
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="rectangular">Rectangular</SelectItem>
                          <SelectItem value="square">Square</SelectItem>
                          <SelectItem value="circular">Circular</SelectItem>
                          <SelectItem value="oval">Oval</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Seats */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-foreground">Seats</label>
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          aria-label="Remove a seat"
                          onClick={() =>
                            applyTableLayoutChange(selectedTableIds, (t) => resizeTableSeats(t, t.seats - 1))
                          }
                          className="h-8 w-8 p-0"
                        >
                          -
                        </Button>
                        <CommitNumberInput
                          min={MIN_TABLE_SEATS}
                          max={MAX_TABLE_SEATS}
                          value={selectedTable?.seats ?? 0}
                          onCommit={(seats) =>
                            applyTableLayoutChange(selectedTableIds, (t) => resizeTableSeats(t, seats))
                          }
                          aria-label="Number of seats"
                          className="h-8 text-sm text-center flex-1"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          aria-label="Add a seat"
                          onClick={() =>
                            applyTableLayoutChange(selectedTableIds, (t) => resizeTableSeats(t, t.seats + 1))
                          }
                          className="h-8 w-8 p-0"
                        >
                          +
                        </Button>
                      </div>
                    </div>

                    {/* Width & Height */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-foreground">Width (m)</label>
                        <CommitNumberInput
                          step="0.1"
                          min={MIN_TABLE_METRES}
                          max={MAX_TABLE_METRES}
                          decimals={1}
                          value={(selectedTable?.width || 150) / 100}
                          onCommit={(metres) =>
                            applyTableLayoutChange(selectedTableIds, (t) =>
                              reflowTable(t, { width: Math.round(metres * 100) })
                            )
                          }
                          className="h-8 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-foreground">Height (m)</label>
                        <CommitNumberInput
                          step="0.1"
                          min={MIN_TABLE_METRES}
                          max={MAX_TABLE_METRES}
                          decimals={1}
                          value={(selectedTable?.height || 150) / 100}
                          onCommit={(metres) =>
                            applyTableLayoutChange(selectedTableIds, (t) =>
                              reflowTable(t, { height: Math.round(metres * 100) })
                            )
                          }
                          className="h-8 text-sm"
                        />
                      </div>
                    </div>

                    {/* Rotation */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-foreground">Rotation (°)</label>
                      <div className="flex gap-1">
                        <CommitNumberInput
                          min={0}
                          max={359}
                          value={selectedTable?.rotation || 0}
                          onCommit={(rotation) => updateSelectedTables({ rotation })}
                          className="h-8 text-sm flex-1"
                        />
                      </div>
                      <div className="flex gap-1">
                        {[0, 90, 180, 270].map(angle => (
                          <Button
                            key={angle}
                            variant="outline"
                            size="sm"
                            onClick={() => updateSelectedTables({ rotation: angle })}
                            className="h-7 text-xs flex-1"
                          >
                            {angle}°
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Table Color */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-foreground">Table Color</label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={selectedTable?.color || '#ffffff'}
                          onChange={(e) => updateSelectedTables({ color: e.target.value })}
                          className="w-12 h-8 p-1 cursor-pointer"
                        />
                        <div className="flex gap-1 flex-wrap flex-1">
                          {['#ffffff', '#8B4513', '#2c3e50', '#e74c3c'].map(color => (
                            <button
                              key={color}
                              type="button"
                              aria-label={`Table colour ${color}`}
                              onClick={() => updateSelectedTables({ color })}
                              className="h-6 w-6 rounded border-2 border-border hover:border-muted-foreground"
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Chair Color */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-foreground">Chair Color</label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={selectedTable?.chairColor || '#e5e7eb'}
                          onChange={(e) => updateSelectedTables({ chairColor: e.target.value })}
                          className="w-12 h-8 p-1 cursor-pointer"
                        />
                        <div className="flex gap-1 flex-wrap flex-1">
                          {['#e5e7eb', '#10b981', '#3b82f6', '#ef4444'].map(color => (
                            <button
                              key={color}
                              type="button"
                              aria-label={`Chair colour ${color}`}
                              onClick={() => updateSelectedTables({ chairColor: color })}
                              className="h-6 w-6 rounded border-2 border-border hover:border-muted-foreground"
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Seat Priority */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-foreground">Seat Priority</label>
                      <CommitNumberInput
                        min={1}
                        max={999}
                        value={selectedTable?.seatNumberPriority || 1}
                        onCommit={(seatNumberPriority) =>
                          setTables(
                            recalculateSeatNumbers(
                              tablesRef.current.map((t) =>
                                selectedTableIds.has(t.id) ? { ...t, seatNumberPriority } : t
                              )
                            )
                          )
                        }
                        className="h-8 text-sm"
                      />
                    </div>

                    {/* Delete Button */}
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        const table = tables.find(t => selectedTableIds.has(t.id));
                        if (table) {
                          const seatedCount = table.seating.filter((seat) => seat.rsvpId).length;
                          confirmAction({
                            title: 'Delete Table',
                            text: `Are you sure you want to delete "${table.name}"?${
                              seatedCount > 0
                                ? ` ${seatedCount} seated guest${seatedCount > 1 ? 's' : ''} will be unassigned.`
                                : ''
                            } You can use Undo to bring it back.`,
                            icon: 'warning'
                          }).then((result) => {
                            if (result.isConfirmed) {
                              const firstSelectedId = Array.from(selectedTableIds)[0];
                              if (firstSelectedId) {
                                handleDeleteTable(firstSelectedId);
                              }
                              showSuccess('Table deleted');
                            }
                          });
                        }
                      }}
                      className="w-full h-8 text-sm"
                    >
                      Delete Table
                    </Button>
                  </div>
                  ) : selectedTableIds.size > 1 ? (
                    <div className="max-w-xs space-y-3 rounded-lg border border-border bg-card p-3 text-card-foreground shadow-md">
                      <h3 className="border-b border-border pb-2 text-sm font-semibold text-foreground">Multiple Tables Selected</h3>
                      <p className="text-sm text-muted-foreground">
                        {selectedTableIds.size} tables selected. Drag to move them together.
                      </p>
                    </div>
                  ) : null}
                </>
              )}
            </div>

            {/* PixiJS stage */}
            <div 
              style={{ 
                width: stageWidth, 
                height: stageHeight, 
                overflow: 'hidden',
                cursor: isDraggingTable ? 'grabbing' : 'grab',
                position: 'relative'
              }}
              onDragOver={handleStageDragOver}
              onDrop={handleStageDrop}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onWheel={handleWheel}
              onMouseMove={() => {
                if (isDraggingSeat || draggedGuest) {
                  const st = stageRef.current;
                  if (st) {
                    const pointerPos = st.getPointerPosition();
                    if (pointerPos) {
                      const container = st.container();
                      const rect = container.getBoundingClientRect();
                      const globalY = rect.top + pointerPos.y;
                      updateUnassignZone(globalY >= 64 && globalY < 144);
                    }
                  }
                }
              }}
            >
              <PixiTablePlannerCanvas
                ref={stageRef}
                playableSize={TABLE_PLANNER_PLAYABLE_AREA_SIZE}
                stageWidth={stageWidth}
                stageHeight={stageHeight}
                scale={scale}
                stagePos={stagePos}
                tables={tables}
                selectedTableIds={selectedTableIds}
                highlightedRelatedGuests={effectiveHighlightedRelatedGuests}
                showGrid={showGrid}
                gridSize={gridSize}
                snapToGrid={snapToGrid}
                reduceDropShadow={isDraggingTable}
                useLocalDragPosition={selectedTableIds.size <= 1}
                isDraggingTable={isDraggingTable}
                isDraggingSeat={isDraggingSeat}
                seatDragSource={seatDragSourceForPixi}
                getGuestName={getGuestName}
                getGuestSeatColours={getGuestSeatColours}
                getGuestPrefixBorderColour={
                  showRankBorders ? getGuestPrefixBorderColour : undefined
                }
                onSelect={handleTableSelect}
                onDragStart={handleTableDragStart}
                onDragMove={handleTableDragMove}
                onDragEnd={handleTableDragEnd}
                onDoubleClick={handleTableDoubleClick}
                onSeatClick={handleSeatClick}
                onSeatDragStart={handleSeatDragStart}
                onSeatDrop={handleSeatDrop}
                onSeatDragCancel={resetSeatDragState}
                onResize={handleTableResize}
                onRotate={handleTableRotate}
                onAdjustSide={adjustSideSeatCount}
                onViewportChange={(x, y) => {
                  isViewportPanningRef.current = false;
                  stagePosRef.current = { x, y };
                  setStagePos({ x, y });
                  scheduleViewportPersist(300);
                }}
                onViewportPanStart={() => {
                  isViewportPanningRef.current = true;
                  cancelPendingViewportPersist();
                }}
                onClearSelection={() => setSelectedTableIds(new Set())}
                registerTableGroup={registerTableGroup}
                surfaceScheme={plannerSurfaceScheme}
              />
            
            {/* Drag Preview - shows seat being dragged */}
            {dragPreview && (
              <div
                ref={dragPreviewElementRef}
                style={{
                  position: 'fixed',
                  left: '-9999px',
                  top: '-9999px',
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: dragPreview.fill,
                  border: `2px solid ${dragPreview.stroke}`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.5,
                  pointerEvents: 'none',
                  zIndex: 10000,
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                }}
              >
                {splitGuestNameIntoSeatLines(dragPreview.guestName || '').map((word, i) => (
                  <div
                    key={`${i}-${word}`}
                    style={{
                      color: '#000000',
                      fontSize: '9px',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      lineHeight: '10px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '44px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {word}
                  </div>
                ))}
              </div>
            )}
            </div>
          </div>

          {/* Sidebar */}
          {(
            <div className="hidden min-h-0 w-96 flex-shrink-0 flex-col border-l border-border bg-card text-card-foreground md:flex">
              <div className="flex flex-1 flex-col min-h-0 p-4">
                {/* Guest List */}
                <Card className="flex min-h-0 flex-1 flex-col">
                  <CardHeader className="flex-shrink-0 pb-3">
                    <CardTitle className="text-sm">Guest List</CardTitle>
                    <CardDescription>
                      {availableGuests.length} unassigned, {assignedGuests.length} seated
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex min-h-0 flex-1 flex-col p-0">
                    <div
                      className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 pb-2 pt-2"
                      style={{ WebkitOverflowScrolling: 'touch' }}
                    >
                      {allGuests.length === 0 ? (
                        <div className="py-8 text-center text-sm text-muted-foreground">
                          No guests found
                        </div>
                      ) : (
                        <div className="w-full space-y-2">
                          {sortedGuests.map((guest) => {
                            const tableInfo = guest.id ? guestSeatAssignments.get(guest.id) ?? null : null;
                            const relationshipColour =
                              guest.id && showGuestRelationships
                                ? (guestSeatColourMap.get(guest.id)?.fill ??
                                  '#d1d5db')
                                : '#d1d5db';

                            return (
                              <div
                                key={guest.id}
                                draggable
                                onDragStart={(e) => handleGuestDragStart(guest, e)}
                                onDragEnd={handleGuestDragEnd}
                                className={`
                                  flex cursor-move items-center justify-between rounded-lg border p-2 transition-colors
                                  hover:bg-muted/50
                                  ${draggedGuest?.id === guest.id ? 'opacity-50' : ''}
                                  ${
                                    tableInfo
                                      ? 'border-emerald-600/40 bg-emerald-500/15 dark:border-emerald-500/50 dark:bg-emerald-950/45'
                                      : 'border-border bg-muted/50'
                                  }
                                `}
                              >
                                <div className="min-w-0 flex flex-1 items-center gap-2">
                                  <span
                                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                                    style={{ backgroundColor: relationshipColour }}
                                    aria-hidden="true"
                                  />
                                  <div className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                                    {formatGuestListDisplayName(guest, ranks)}
                                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                                      {tableInfo
                                        ? `${
                                            tableInfo.roomId !== tablesRoomIdRef.current
                                              ? `${tableInfo.roomName} • `
                                              : ''
                                          }${tableInfo.tableName} • Seat ${tableInfo.seatNumber}`
                                        : 'Unassigned'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {showRankBorders && prefixBorderLegend.length > 0 && (
                  <div className="mt-3 flex-shrink-0">
                    <p className="mb-1.5 text-xs font-semibold text-muted-foreground">Seat border by prefix</p>
                    <div className="flex flex-wrap gap-2">
                      {prefixBorderLegend.map((entry) => (
                        <span
                          key={entry.label}
                          className="inline-flex items-center gap-1.5 text-xs text-foreground"
                        >
                          <span
                            className="inline-block h-3 w-3 rounded-full border-2"
                            style={{ borderColor: entry.borderColour, backgroundColor: 'transparent' }}
                          />
                          {entry.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        )}
      </main>


      <Dialog open={showVersionsModal} onOpenChange={setShowVersionsModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Table plan versions</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto pr-1">
            {versionsLoading ? (
              <p className="py-4 text-sm text-muted-foreground">Loading versions...</p>
            ) : versionsAccessDenied ? (
              <p className="py-4 text-sm text-muted-foreground">
                Versions are unavailable for your account in this environment.
              </p>
            ) : tablePlanVersions.length === 0 ? (
              <p className="py-4 text-sm text-muted-foreground">No saved versions yet.</p>
            ) : (
              <div className="space-y-2">
                {tablePlanVersions.map((version) => (
                  <div
                    key={version.id}
                    className="flex items-center justify-between rounded-md border p-3"
                  >
                    <div>
                      <p className="text-sm font-medium">
                        {new Date(version.createdAt).toLocaleString()}
                      </p>
                      <p className="text-xs text-muted-foreground">Preview: {version.tables.length} tables</p>
                      <p className="text-xs text-muted-foreground">
                        Last updated: {new Date(version.lastUpdated).toLocaleString()}
                        {version.reason ? ` • ${version.reason.replace('-', ' ')}` : ''}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void handleRestoreVersion(version)}
                    >
                      Restore
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modals */}
      {configModalTable && (
        <TableConfigModal
          table={configModalTable}
          onClose={() => setConfigModalTable(null)}
          onSave={handleSaveTableConfig}
          onUpdate={handleUpdateTableConfig}
          onDelete={handleDeleteTable}
          getGuestName={(id) => guestNameMap.get(id) ?? null}
        />
      )}

      {guestAssignmentModal && (
        <GuestAssignmentModal
          tableId={guestAssignmentModal.tableId}
          tableName={guestAssignmentModal.tableName}
          seatNumber={guestAssignmentModal.seatNumber}
          currentGuest={guestAssignmentModal.currentGuest}
          allGuests={allGuests}
          availableGuests={availableGuests}
          onClose={() => setGuestAssignmentModal(null)}
          onAssign={handleAssignGuest}
          onUnassign={handleUnassignGuest}
        />
      )}

      {showPDFExportModal && (
        <PDFExportModal
          onClose={() => setShowPDFExportModal(false)}
          guests={allGuests}
          tables={tables}
          allRoomTables={effectiveRoomTables}
          rooms={rooms}
          eventId={eventId}
          unitId={unitId}
        />
      )}

      {showAutoPopulateModal && (
        <AutoPopulateModal
          isOpen={showAutoPopulateModal}
          onClose={() => setShowAutoPopulateModal(false)}
          onAutoPopulateAll={() => autoPopulateGuests(true)}
          onAutoPopulateUnassigned={() => autoPopulateGuests(false)}
        />
      )}

      {/* New design plan modal */}
      {showCreateRoomModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            role="dialog"
            aria-modal="true"
            aria-label="New design plan"
            className="mx-4 w-96 max-w-md rounded-lg border border-border bg-card p-6 text-card-foreground"
          >
            <h2 className="mb-4 text-xl font-bold text-foreground">
              {rooms.length === 0 ? 'Create your first design plan' : 'New design plan'}
            </h2>
            {rooms.length === 0 && (
              <p className="mb-4 text-sm text-muted-foreground">
                Add a named floor plan to start designing. You can create as many as you need and switch between them on the ribbon above.
              </p>
            )}
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-foreground">
                  Design name
                </label>
                <Input
                  value={newRoomName}
                  onChange={(e) => setNewRoomName(e.target.value)}
                  placeholder="e.g. Main hall, Evening layout, Plan A…"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      createRoom();
                    }
                  }}
                  autoFocus
                />
              </div>
              <div className="flex gap-2 justify-end">
                {rooms.length > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCreateRoomModal(false);
                      setNewRoomName('');
                    }}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  onClick={createRoom}
                  disabled={!newRoomName.trim()}
                >
                  Create design plan
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {renameTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Rename floor plan"
            className="mx-4 w-96 max-w-md rounded-lg border border-border bg-card p-6 text-card-foreground"
          >
            <h2 className="mb-4 text-xl font-bold text-foreground">Rename floor plan</h2>
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-foreground">
                  Design name
                </label>
                <Input
                  value={renameTarget.name}
                  onChange={(e) =>
                    setRenameTarget((prev) =>
                      prev ? { ...prev, name: e.target.value } : prev
                    )
                  }
                  placeholder="Design name"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      void renameDesign();
                    }
                  }}
                  autoFocus
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setRenameTarget(null)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => void renameDesign()}
                  disabled={!renameTarget.name.trim()}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
